import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Tone from 'tone';
import { collection, doc, getDoc, increment, onSnapshot, query, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import { db, appId } from '../../../firebase-config';

import { getActiveCompanyId } from '../../../core/company/companyConfig';
import {
  resolveContactsCollectionPath,
  resolveCompanyMainSettingsPath,
  resolveCompanyOfficeSettingsPath,
  resolveRouteDocPath,
  resolveScanEventsCollectionPath,
  resolveServiceClosureDocPath,
  resolveServiceDocPath,
  resolveStockMovementsCollectionPath,
  resolveStockOutboundOrderDocPath,
  resolveStockProductDocPath,
  resolveStockPurchaseDocPath,
  resolveStockPurchaseLineDocPath,
  resolveStockPurchaseLinesCollectionPath
} from '../../../core/company/companyPaths';
import {
  buildClosurePeriod,
  buildServiceClosureKey,
  resolveContractBillingCadence,
  resolveServiceRecognitionBadge,
} from '../../service/serviceBilling';

import MapComponent from './MapComponent';
import DestinationDetailModal from './DestinationDetailModal';
import DestinationFormModal from './DestinationFormModal';
import CancelRouteModal from './CancelRouteModal';
import StartTripModal from './StartTripModal';
import NextDestinationModal from './NextDestinationModal';

import {
  calculateRouteCost,
  Icon,
  useIsMobile,
  generateLinkColors,
  getRouteStatus,
  buildRouteRoadMetrics,
  getRouteResolvedRoadSummary,
  getRouteRoadMetrics
} from './LogisticsUtils';
import { getOfficeLocationFromSettings } from '../../reports/utils/routeMonitorUtils';
import { buildDriverDisplayName, buildTransportDisplayName, buildTransportMeta } from './driverUtils';
import { buildOutboundOrderHeaderSummary, buildPurchaseHeaderSummary } from '../../stock/stockFlowUtils';

const DEFAULT_COMPANY_ID = 'default';

// =========================
// Helpers contactos
// =========================
const normalizeString = (value) => (value || '').toString().trim();

const normalizeType = (type) => {
  const normalized = (type || '').toString().trim().toLowerCase();
  return ['cliente', 'proveedor', 'transporte'].includes(normalized)
    ? normalized
    : 'cliente';
};

const resolveRealizedCommercialSnapshot = ({
  service = {},
  realizedOperationalCost = 0,
}) => {
  const commercialModel = String(service?.commercialModel || '').trim().toLowerCase();
  const pricingRule = String(service?.pricingRule || '').trim().toLowerCase();
  const billingCadence =
    String(service?.billingCadenceSnapshot || '').trim().toLowerCase() ||
    resolveContractBillingCadence(service);
  const manualTotal =
    Number(
      service?.agreedPricePerTripSnapshot ??
      service?.agreedPricePerTrip ??
      service?.salePrice ??
      0
    ) || 0;
  const fixedMarginAmount =
    Number(service?.fixedMarginAmountSnapshot ?? service?.fixedMarginAmount ?? 0) || 0;
  const marginPercent =
    Number(service?.marginPercentSnapshot ?? service?.marginPercent ?? 0) || 0;
  const defaultMarginPercent = marginPercent > 0 ? marginPercent : 40;

  if (commercialModel === 'monthly_fixed' && billingCadence !== 'per_trip') {
    return {
      realizedSalePrice: 0,
      realizedMargin: 0,
      financialRecognitionMode: billingCadence || 'monthly',
      closureChargeMode: 'monthly_fixed',
    };
  }

  if (pricingRule === 'fixed_margin') {
    const realizedMargin = fixedMarginAmount;
    return {
      realizedSalePrice: realizedOperationalCost + realizedMargin,
      realizedMargin,
      financialRecognitionMode: billingCadence === 'per_trip' ? 'per_trip' : billingCadence,
      closureChargeMode: 'per_trip',
    };
  }

  if (pricingRule === 'manual_total') {
    const realizedSalePrice = Math.max(0, manualTotal);
    return {
      realizedSalePrice,
      realizedMargin: realizedSalePrice - realizedOperationalCost,
      financialRecognitionMode: billingCadence === 'per_trip' ? 'per_trip' : billingCadence,
      closureChargeMode: 'per_trip',
    };
  }

  const realizedMargin = realizedOperationalCost * (defaultMarginPercent / 100);
  return {
    realizedSalePrice: realizedOperationalCost + realizedMargin,
    realizedMargin,
    financialRecognitionMode:
      billingCadence === 'per_trip'
        ? 'per_trip'
        : billingCadence || (commercialModel === 'monthly_variable' ? 'monthly' : 'per_trip'),
    closureChargeMode: commercialModel === 'monthly_fixed' ? 'monthly_fixed' : 'per_trip',
  };
};

const buildContactKey = (contact) => {
  const name = normalizeString(contact?.name).toLowerCase();
  const type = normalizeType(contact?.type);
  return `${name}::${type}`;
};

const normalizeAddresses = (addresses = []) => {
  if (!Array.isArray(addresses)) return [];

  return addresses.map((addr) => ({
    address: addr?.address || '',
    apartment: addr?.apartment || '',
    locality: addr?.locality || '',
    province: addr?.province || 'CABA',
    partido: addr?.partido || '',
    postalCode: addr?.postalCode || '',
    latlng: addr?.latlng || null
  }));
};

const normalizeContactPeople = (contacts = []) => {
  if (!Array.isArray(contacts)) return [];

  return contacts.map((c) => ({
    name: c?.name || '',
    phone: c?.phone || ''
  }));
};

const mergeContacts = (companyContacts, legacyContacts) => {
  const mergedMap = new Map();

  legacyContacts.forEach((contact) => {
    mergedMap.set(buildContactKey(contact), contact);
  });

  companyContacts.forEach((contact) => {
    mergedMap.set(buildContactKey(contact), contact);
  });

  return Array.from(mergedMap.values()).sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
  );
};

const getEventTimestampMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const formatTraceEventDateTime = (value) => {
  const millis = getEventTimestampMillis(value);
  if (!millis) return 'Sin hora';
  return new Date(millis).toLocaleString('es-AR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const buildTraceEventMeta = (event = {}) => {
  const status = String(event?.resultStatus || '').trim().toLowerCase();
  const evidenceType = String(event?.evidenceType || '').trim().toLowerCase();
  const source = String(event?.source || '').trim().toLowerCase();
  const scannedValue = String(event?.scannedValue || event?.rawValue || '').trim();
  const targetLabel = String(event?.targetLabel || '').trim();

  if (status === 'evidence_uploaded' || evidenceType === 'photo' || source === 'photo_upload') {
    return {
      tone: 'cyan',
      badge: 'Foto',
      title: 'Evidencia fotográfica cargada',
      detail: targetLabel || 'El chofer dejó respaldo visual en este destino.',
      iconPath: 'M3.375 6.375A2.625 2.625 0 016 3.75h12a2.625 2.625 0 012.625 2.625v11.25A2.625 2.625 0 0118 20.25H6a2.625 2.625 0 01-2.625-2.625V6.375zm11.25 1.875a1.125 1.125 0 100-2.25 1.125 1.125 0 000 2.25zm-8.25 8.625h11.25l-3.11-4.148a.75.75 0 00-1.18-.03l-2.22 2.664-1.47-1.47a.75.75 0 00-1.138.086L6.375 16.875z'
    };
  }

  if (status === 'found') {
    return {
      tone: 'emerald',
      badge: source === 'camera_barcode_detector' ? 'QR / Barra' : 'Escaneo',
      title: 'Código validado correctamente',
      detail: scannedValue || targetLabel || 'El código coincide con la operación esperada.',
      iconPath: 'M3.75 4.5h3v3h-3v-3zm0 12h3v3h-3v-3zm12-12h3v3h-3v-3zm0 6h3v3h-3v-3zm-6 0h3v3h-3v-3zm0 6h3v3h-3v-3z'
    };
  }

  if (status === 'multiple') {
    return {
      tone: 'amber',
      badge: 'Escaneo',
      title: 'Código con varias coincidencias',
      detail: scannedValue || 'Hace falta revisar manualmente la validación.',
      iconPath: 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 6h.008v.008H12v-.008z'
    };
  }

  if (status === 'not_found') {
    return {
      tone: 'rose',
      badge: 'Escaneo',
      title: 'Código sin coincidencia operativa',
      detail: scannedValue || 'El escaneo quedó registrado para revisión.',
      iconPath: 'M12 9v3.75m0 3.75h.008v.008H12v-.008zm9-3.758a9 9 0 11-18 0 9 9 0 0118 0z'
    };
  }

  return {
    tone: 'slate',
    badge: 'Evento',
    title: 'Acción operativa registrada',
    detail: scannedValue || targetLabel || 'Quedó un evento en la trazabilidad del destino.',
    iconPath: 'M11.25 6.75h7.5m-7.5 4.5h7.5m-7.5 4.5h4.5m-9-9h.008v.008H6.75V6.75zm0 4.5h.008v.008H6.75v-.008zm0 4.5h.008v.008H6.75v-.008z'
  };
};


const resolveCompanyContactsPathSafe = (companyId) => {
  try {
    return resolveContactsCollectionPath(companyId);
  } catch (errorByString) {
    try {
      return resolveContactsCollectionPath({ appId, companyId });
    } catch (errorByObject) {
      console.warn(
        'No se pudo resolver path de contactos por empresa. Se usa fallback legacy.',
        { errorByString, errorByObject, companyId }
      );
      return `artifacts/${appId}/public/data/contacts`;
    }
  }
};

// =========================
// Helpers UI
// =========================
const InfoBadge = ({ children, tone = 'slate', compact = false }) => {
  const tones = {
    slate: 'border-slate-200 bg-slate-100 text-slate-700',
    blue: 'border-blue-200 bg-blue-100 text-blue-700',
    cyan: 'border-cyan-200 bg-cyan-100 text-cyan-700',
    emerald: 'border-emerald-200 bg-emerald-100 text-emerald-700',
    amber: 'border-amber-200 bg-amber-100 text-amber-700',
    rose: 'border-rose-200 bg-rose-100 text-rose-700',
    violet: 'border-violet-200 bg-violet-100 text-violet-700',
    orange: 'border-orange-200 bg-orange-100 text-orange-700'
  };

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-semibold ${compact ? 'px-1.5 py-[2px] text-[8px]' : 'px-2.5 py-1 text-[11px]'} ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
};

const SERVICE_BADGE_THEMES = [
  {
    shell: 'border-cyan-200 bg-cyan-50/90',
    text: 'text-cyan-800',
    soft: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  },
  {
    shell: 'border-violet-200 bg-violet-50/90',
    text: 'text-violet-800',
    soft: 'bg-violet-100 text-violet-800 border-violet-200',
  },
  {
    shell: 'border-emerald-200 bg-emerald-50/90',
    text: 'text-emerald-800',
    soft: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
  {
    shell: 'border-amber-200 bg-amber-50/90',
    text: 'text-amber-800',
    soft: 'bg-amber-100 text-amber-800 border-amber-200',
  },
];

const getServiceBadgeTheme = (serviceId = '') => {
  const seed = String(serviceId || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return SERVICE_BADGE_THEMES[seed % SERVICE_BADGE_THEMES.length];
};

const ActionButton = ({ children, onClick, tone = 'slate', iconPath, className = '', disabled = false, title = '' }) => {
  const tones = {
    slate: 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-100',
    dark: 'bg-slate-900 text-white hover:bg-slate-800',
    blue: 'bg-blue-600 text-white hover:bg-blue-700',
    cyan: 'bg-cyan-600 text-white hover:bg-cyan-700',
    emerald: 'bg-emerald-600 text-white hover:bg-emerald-700',
    amber: 'bg-amber-500 text-white hover:bg-amber-600',
    rose: 'bg-rose-600 text-white hover:bg-rose-700',
    violet: 'bg-violet-600 text-white hover:bg-violet-700'
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-bold shadow-sm transition ${tones[tone] || tones.slate} ${disabled ? 'cursor-not-allowed opacity-50' : ''} ${className}`}
    >
      {iconPath ? <Icon path={iconPath} className="h-4 w-4" /> : null}
      {children}
    </button>
  );
};

const getRouteDestinationReference = (destinationId, routeDestinations = []) => {
  if (!destinationId) return 'A definir';
  if (destinationId === 'pending') return 'Pendiente';
  if (destinationId === 'oficina') return 'Oficina';

  const linkedDestination = routeDestinations.find((item) => item.id === destinationId);
  if (!linkedDestination) return 'Otro destino';

  return `${Number(linkedDestination.order ?? 0) + 1}. ${linkedDestination.name || 'Destino'}`;
};

const getDestinationStatusMeta = (status) => {
  switch (status) {
    case 'completed':
      return {
        label: 'Completado',
        tone: 'emerald',
        shell: 'border-emerald-200 bg-emerald-50/70'
      };
    case 'incomplete':
      return {
        label: 'Incompleto',
        tone: 'amber',
        shell: 'border-amber-200 bg-amber-50/80'
      };
    case 'cancelled':
      return {
        label: 'Cancelado',
        tone: 'rose',
        shell: 'border-rose-200 bg-rose-50/80'
      };
    case 'en_destino':
      return {
        label: 'En destino',
        tone: 'blue',
        shell: 'border-blue-200 bg-blue-50/80'
      };
    case 'en_camino':
      return {
        label: 'En camino',
        tone: 'cyan',
        shell: 'border-cyan-300 bg-cyan-50/90'
      };
    default:
      return {
        label: 'Pendiente',
        tone: 'slate',
        shell: 'border-slate-200 bg-white'
      };
  }
};

const getDestinationMiniPillClass = (status) => {
  switch (status) {
    case 'completed':
      return 'border-emerald-500 bg-emerald-500 text-white shadow-[0_8px_16px_rgba(16,185,129,0.28)]';
    case 'incomplete':
      return 'border-amber-400 bg-amber-400 text-slate-950 shadow-[0_8px_16px_rgba(251,191,36,0.26)]';
    case 'cancelled':
      return 'border-rose-500 bg-rose-500 text-white shadow-[0_8px_16px_rgba(244,63,94,0.24)]';
    case 'en_destino':
      return 'border-blue-500 bg-blue-500 text-white shadow-[0_8px_16px_rgba(59,130,246,0.24)]';
    case 'en_camino':
      return 'border-cyan-500 bg-cyan-500 text-white shadow-[0_8px_16px_rgba(6,182,212,0.24)]';
    default:
      return 'border-slate-900 bg-slate-900 text-white shadow-[0_8px_16px_rgba(15,23,42,0.26)]';
  }
};

const buildLiveRouteDocumentRef = (route = {}, companyId = DEFAULT_COMPANY_ID) => {
  if (!route?.id) return null;
  if (route?._source === 'legacy') {
    return doc(db, `artifacts/${appId}/logisticsRoutes/${route.id}`);
  }
  return doc(db, resolveRouteDocPath(companyId, route.id));
};

const buildCompanyRouteDocumentRef = (route = {}, companyId = DEFAULT_COMPANY_ID) => {
  if (!route?.id || !companyId) return null;
  return doc(db, resolveRouteDocPath(companyId, route.id));
};

const buildLegacyRouteDocumentRef = (route = {}) => {
  const legacyId = route?.legacySourceId || route?.id || null;

  if (!legacyId) return null;
  return doc(db, `artifacts/${appId}/logisticsRoutes/${legacyId}`);
};

// =========================
// Vista detalle
// =========================
const RouteDetail = ({
  route: initialRoute,
  showMessage,
  addLogisticsNotification,
  onBack,
  onBackToHistory,
  userRole,
  transporterLocation,
  transporterPath,
  onBackToDashboard,
  companyId: companyIdProp,
  showEmbeddedMap,
  onRouteClosed
}) => {
  const fallbackCompanyId = getActiveCompanyId() || DEFAULT_COMPANY_ID;
  const resolvedCompanyId =
    initialRoute?.companyId ||
    companyIdProp ||
    fallbackCompanyId;

  const [route, setRoute] = useState(initialRoute || {});
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [stockPurchaseLines, setStockPurchaseLines] = useState([]);
  const [stockMovements, setStockMovements] = useState([]);
  const [processingInboundValidation, setProcessingInboundValidation] = useState(false);
  const [statusNotification, setStatusNotification] = useState({ id: null, type: '' });
  const [draggedItem, setDraggedItem] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showStartTripModal, setShowStartTripModal] = useState(false);
  const [showNextDestinationModal, setShowNextDestinationModal] = useState(false);
  const [companyMainSettings, setCompanyMainSettings] = useState(null);
  const [companyOfficeSettings, setCompanyOfficeSettings] = useState(null);
  const [routeTraceEvents, setRouteTraceEvents] = useState([]);
  const [collapsedDestinationIds, setCollapsedDestinationIds] = useState(() => new Set());
  const [isGeneralListView, setIsGeneralListView] = useState(false);

  const isMobile = useIsMobile();
  const shouldShowEmbeddedMap = !isMobile && (typeof showEmbeddedMap === 'boolean' ? showEmbeddedMap : userRole !== 'transportista');
  const synth = useRef(null);
  const prevRouteRef = useRef(null);
  const previousDestinationsRef = useRef([]);
  const roadMetricsHydrationRef = useRef(false);
  const autoCollapseDestinationsTimeoutRef = useRef(null);
  const autoCollapsedRouteIdRef = useRef(null);
  const destinationItemRefs = useRef({});
  const destinationListContainerRef = useRef(null);

  const routeDestinations = route?.destinations || [];
  const isTransporterView = userRole === 'transportista';
  const canManageRoute = !isTransporterView;
  const validRouteDestinations = useMemo(
    () => routeDestinations.filter((dest) => dest?.latlng && typeof dest.latlng.lat === 'number' && typeof dest.latlng.lng === 'number'),
    [routeDestinations]
  );
  const validRouteDestinationsCount = validRouteDestinations.length;
  const resolvedOfficeLocation = useMemo(
    () =>
      getOfficeLocationFromSettings({
        officeLocation: companyMainSettings?.officeLocation || {},
        office: companyOfficeSettings || {}
      }),
    [companyMainSettings, companyOfficeSettings]
  );
  const routeRoadSignature = useMemo(
    () => validRouteDestinations
      .map((dest) => `${dest.id || ''}:${Number(dest.order ?? 0)}:${Number(dest?.latlng?.lat || 0).toFixed(6)}:${Number(dest?.latlng?.lng || 0).toFixed(6)}`)
      .join('|'),
    [validRouteDestinations]
  );
  const routeRoadMetrics = useMemo(() => getRouteRoadMetrics(route), [route]);
  const hasPendingDestinations = routeDestinations.some((destination) => destination.status === 'pending');
  const isFreshInProgressRoute =
    route?.status === 'in_progress' &&
    routeDestinations.length > 0 &&
    routeDestinations.every((destination) => destination.status === 'pending');

  const syncRouteDocuments = useCallback(
    async (payload) => {
      const liveRouteRef = buildLiveRouteDocumentRef(route, resolvedCompanyId);
      if (!liveRouteRef) {
        throw new Error('missing-route-ref');
      }

      const routePayload = isTransporterView
        ? {
            ...payload,
            updatedAt: new Date(),
            lastDriverWriteAt: new Date(),
            lastDriverWriteSource: 'transporter-view',
            lastDriverTransportId: route?.transporterId || route?.transportId || '',
            lastDriverTransportSlug: route?.transporterSlug || route?.driverAccessSlug || route?.slug || '',
            lastDriverId: route?.driverId || '',
          }
        : payload;

      const syncBatch = writeBatch(db);
      syncBatch.update(liveRouteRef, routePayload);

      const companyRouteRef = buildCompanyRouteDocumentRef(
        route,
        route?.companyId || resolvedCompanyId
      );
      if (companyRouteRef && companyRouteRef.path !== liveRouteRef.path) {
        syncBatch.set(
          companyRouteRef,
          {
            ...routePayload,
            legacySourceId: route?.legacySourceId || route?.id,
            updatedAt: new Date()
          },
          { merge: true }
        );
      }

      const legacyRouteRef = buildLegacyRouteDocumentRef(route);
      if (legacyRouteRef && legacyRouteRef.path !== liveRouteRef.path) {
        syncBatch.set(
          legacyRouteRef,
          {
            ...routePayload,
            companyId: route?.companyId || resolvedCompanyId,
            legacySourceId: route?.legacySourceId || route?.id || legacyRouteRef.id,
            updatedAt: new Date()
          },
          { merge: true }
        );
      }

      await syncBatch.commit();
    },
    [route, resolvedCompanyId]
  );

  const syncLinkedServiceExecution = useCallback(
    async (destinationSnapshot, destinationsSnapshot = routeDestinations) => {
      const serviceId = String(destinationSnapshot?.serviceId || '').trim();
      const operationRole = String(destinationSnapshot?.operationRole || '').trim().toLowerCase();
      const destinationStatus = String(destinationSnapshot?.status || '').trim().toLowerCase();

      if (!serviceId || operationRole !== 'delivery') return;
      if (!['completed', 'cancelled'].includes(destinationStatus)) return;

      const resolvedCompletedAt =
        destinationSnapshot?.completedAt && typeof destinationSnapshot.completedAt.toDate === 'function'
          ? destinationSnapshot.completedAt.toDate()
          : destinationSnapshot?.completedAt || new Date();

      const linkedDeliveryStops = (destinationsSnapshot || []).filter((destination) => {
        const destinationServiceId = String(destination?.serviceId || '').trim();
        const destinationRole = String(destination?.operationRole || '').trim().toLowerCase();
        return destinationServiceId === serviceId && destinationRole === 'delivery';
      });

      const allStopsCompleted = linkedDeliveryStops.length > 0
        ? linkedDeliveryStops.every((destination) => String(destination?.status || '').trim().toLowerCase() === 'completed')
        : destinationStatus === 'completed';
      const anyStopCancelled = linkedDeliveryStops.some(
        (destination) => String(destination?.status || '').trim().toLowerCase() === 'cancelled'
      );

      const serviceRef = doc(db, resolveServiceDocPath(resolvedCompanyId, serviceId));

      await updateDoc(serviceRef, {
        status:
          destinationStatus === 'cancelled'
            ? 'cancelled'
            : allStopsCompleted
            ? 'completed'
            : 'assigned',
        statusLabel:
          destinationStatus === 'cancelled'
            ? 'Cancelado'
            : allStopsCompleted
            ? 'Realizado'
            : 'Asignado',
        routeId: route?.id || '',
        routeNameSnapshot: route?.name || '',
        driverId: route?.driverId || route?.currentDriverId || '',
        driverNameSnapshot: buildDriverDisplayName(route, ''),
        transportId: route?.transportId || route?.transporterId || '',
        transportNameSnapshot: buildTransportDisplayName(route, ''),
        completedAt: destinationStatus === 'completed' && allStopsCompleted ? resolvedCompletedAt : null,
        cancelledAt: destinationStatus === 'cancelled' || anyStopCancelled ? resolvedCompletedAt : null,
        updatedAt: new Date()
      });
    },
    [resolvedCompanyId, route, routeDestinations]
  );

  const syncLinkedServicesFinancialClosure = useCallback(
    async (destinationsSnapshot = routeDestinations, completedAt = new Date()) => {
      const linkedServiceIds = Array.from(
        new Set(
          (destinationsSnapshot || [])
            .map((destination) => String(destination?.serviceId || '').trim())
            .filter(Boolean)
        )
      );

      if (linkedServiceIds.length === 0) return;

      const routeSnapshot = {
        ...route,
        destinations: destinationsSnapshot,
      };
      const liquidationSettings = companyMainSettings?.liquidationSettings || companyMainSettings || {};
      const { totalCost, legCosts } = calculateRouteCost({
        route: routeSnapshot,
        settings: liquidationSettings,
        officeLocation: resolvedOfficeLocation,
      });

      const routeDestinationsWithCost = (destinationsSnapshot || []).filter(
        (destination) =>
          ['completed', 'incomplete'].includes(String(destination?.status || '').trim().toLowerCase()) &&
          Number(legCosts?.[destination?.id] || 0) >= 0
      );

      const totalLegCost = routeDestinationsWithCost.reduce(
        (acc, destination) => acc + (Number(legCosts?.[destination?.id] || 0) || 0),
        0
      );
      const extraRouteCost = Math.max(0, Number(totalCost || 0) - totalLegCost);

      const serviceBaseCosts = linkedServiceIds.reduce((acc, serviceId) => {
        const linkedCost = routeDestinationsWithCost.reduce((sum, destination) => {
          return String(destination?.serviceId || '').trim() === serviceId
            ? sum + (Number(legCosts?.[destination?.id] || 0) || 0)
            : sum;
        }, 0);
        acc[serviceId] = linkedCost;
        return acc;
      }, {});

      const totalServiceBaseCost = linkedServiceIds.reduce(
        (acc, serviceId) => acc + (Number(serviceBaseCosts?.[serviceId] || 0) || 0),
        0
      );

      await Promise.all(
        linkedServiceIds.map(async (serviceId) => {
          const serviceRef = doc(db, resolveServiceDocPath(resolvedCompanyId, serviceId));
          const serviceSnap = await getDoc(serviceRef);
          if (!serviceSnap.exists()) return;

          const serviceData = serviceSnap.data() || {};
          const billingCadence =
            String(serviceData?.billingCadenceSnapshot || '').trim().toLowerCase() ||
            resolveContractBillingCadence(serviceData);
          const allocationWeight =
            totalServiceBaseCost > 0
              ? (Number(serviceBaseCosts?.[serviceId] || 0) || 0) / totalServiceBaseCost
              : 1 / Math.max(1, linkedServiceIds.length);
          const realizedOperationalCost =
            (Number(serviceBaseCosts?.[serviceId] || 0) || 0) + extraRouteCost * allocationWeight;
          const commercialSnapshot = resolveRealizedCommercialSnapshot({
            service: serviceData,
            realizedOperationalCost,
          });

          const isPerTripRecognition = billingCadence === 'per_trip';
          const closurePeriod = buildClosurePeriod(completedAt, billingCadence);
          const pendingClosureKey = isPerTripRecognition
            ? ''
            : buildServiceClosureKey({
                clientId: serviceData?.clientId,
                contractId: serviceData?.contractId,
                cadence: billingCadence,
                periodStart: closurePeriod.periodStart,
              });

          const revenueRecognizedAt = isPerTripRecognition ? completedAt : null;
          const recognizedInBalance = isPerTripRecognition;
          const recognitionPending = !isPerTripRecognition;
          const recognizedSalePrice = isPerTripRecognition
            ? Number(commercialSnapshot.realizedSalePrice || 0) || 0
            : null;
          const recognizedMargin = isPerTripRecognition
            ? Number(commercialSnapshot.realizedMargin || 0) || 0
            : null;

          await updateDoc(serviceRef, {
            routeId: route?.id || '',
            routeNameSnapshot: route?.name || '',
            realizedOperationalCost,
            realizedMargin: Number(commercialSnapshot.realizedMargin || 0) || 0,
            realizedSalePrice: Number(commercialSnapshot.realizedSalePrice || 0) || 0,
            financialRecognitionMode: commercialSnapshot.financialRecognitionMode || 'per_trip',
            financialClosedAt: completedAt,
            revenueRecognizedAt,
            recognizedAt: revenueRecognizedAt,
            recognizedInBalance,
            recognitionPending,
            recognizedSalePrice,
            recognizedMargin,
            pendingClosureKey,
            pendingClosurePeriodStart: recognitionPending ? closurePeriod.periodStart.toISOString() : null,
            pendingClosurePeriodEnd: recognitionPending ? closurePeriod.periodEnd.toISOString() : null,
            updatedAt: new Date(),
          });

          if (!recognitionPending) return;

          const closureRef = doc(
            db,
            resolveServiceClosureDocPath(resolvedCompanyId, pendingClosureKey)
          );
          const closureSnap = await getDoc(closureRef);
          const existingClosure = closureSnap.exists() ? closureSnap.data() || {} : {};
          const existingLinkedServiceIds = Array.isArray(existingClosure?.linkedServiceIds)
            ? existingClosure.linkedServiceIds.filter(Boolean)
            : [];
          const nextLinkedServiceIds = Array.from(new Set([...existingLinkedServiceIds, serviceId]));

          const serviceIdsForTotals = closureSnap.exists()
            ? nextLinkedServiceIds
            : [serviceId];

          const serviceDocsForTotals = await Promise.all(
            serviceIdsForTotals.map(async (linkedId) => {
              if (linkedId === serviceId) {
                return {
                  id: linkedId,
                  ...serviceData,
                  realizedOperationalCost,
                  realizedMargin: Number(commercialSnapshot.realizedMargin || 0) || 0,
                  realizedSalePrice: Number(commercialSnapshot.realizedSalePrice || 0) || 0,
                  billingCadenceSnapshot: billingCadence,
                };
              }
              const linkedSnap = await getDoc(doc(db, resolveServiceDocPath(resolvedCompanyId, linkedId)));
              return linkedSnap.exists() ? { id: linkedId, ...linkedSnap.data() } : null;
            })
          );

          const validServiceDocs = serviceDocsForTotals.filter(Boolean);
          const operationalCostTotal = validServiceDocs.reduce(
            (acc, item) => acc + Math.max(0, Number(item?.realizedOperationalCost || 0)),
            0
          );

          let saleTotalPending = validServiceDocs.reduce(
            (acc, item) => acc + Math.max(0, Number(item?.realizedSalePrice || 0)),
            0
          );

          if (String(serviceData?.commercialModel || '').trim().toLowerCase() === 'monthly_fixed' && billingCadence !== 'per_trip') {
            saleTotalPending = Number(serviceData?.monthlyFeeSnapshot ?? serviceData?.monthlyFee ?? 0) || 0;
          } else if (
            validServiceDocs.some((item) => String(item?.commercialModel || '').trim().toLowerCase() === 'monthly_fixed') &&
            billingCadence !== 'per_trip'
          ) {
            saleTotalPending = validServiceDocs.reduce((acc, item) => {
              if (String(item?.commercialModel || '').trim().toLowerCase() === 'monthly_fixed') {
                return acc + (Number(item?.monthlyFeeSnapshot ?? item?.monthlyFee ?? 0) || 0);
              }
              return acc + Math.max(0, Number(item?.realizedSalePrice || 0));
            }, 0);
          }

          const marginTotalPending = saleTotalPending - operationalCostTotal;

          await setDoc(
            closureRef,
            {
              companyId: resolvedCompanyId,
              clientId: String(serviceData?.clientId || '').trim(),
              clientNameSnapshot: serviceData?.clientNameSnapshot || serviceData?.clientName || '',
              contractId: String(serviceData?.contractId || '').trim(),
              contractNameSnapshot: serviceData?.contractNameSnapshot || '',
              billingCadence,
              billingCadenceLabel: resolveServiceRecognitionBadge({
                billingCadenceSnapshot: billingCadence,
                recognitionPending: true,
              }).label.split(' · ')[0],
              periodStart: closurePeriod.periodStart.toISOString(),
              periodEnd: closurePeriod.periodEnd.toISOString(),
              periodLabel: closurePeriod.label,
              status: 'ready_to_bill',
              statusLabel: 'Listo para cobrar',
              linkedServiceIds: nextLinkedServiceIds,
              tripCount: nextLinkedServiceIds.length,
              operationalCostTotal,
              saleTotalPending,
              marginTotalPending,
              notes: existingClosure?.notes || '',
              updatedAt: new Date(),
              createdAt: existingClosure?.createdAt || new Date(),
              paidAt: existingClosure?.paidAt || null,
              recognizedAt: existingClosure?.recognizedAt || null,
            },
            { merge: true }
          );
        })
      );
    },
    [companyMainSettings, resolvedCompanyId, resolvedOfficeLocation, route, routeDestinations]
  );

  const routeProgress = useMemo(() => {
    const total = routeDestinations.length;
    const completed = routeDestinations.filter((destination) => destination.status === 'completed').length;
    const incomplete = routeDestinations.filter((destination) => destination.status === 'incomplete').length;
    const cancelled = routeDestinations.filter((destination) => destination.status === 'cancelled').length;
    const pending = Math.max(0, total - completed - incomplete - cancelled);

    return { total, completed, incomplete, cancelled, pending };
  }, [routeDestinations]);


  const linkedPurchaseLoadsByLineId = useMemo(() => {
    const aggregated = new Map();

    routeDestinations.forEach((destination) => {
      (destination?.operationalLoads || []).forEach((load) => {
        if (String(load?.stockMode || '').trim().toLowerCase() !== 'pending_inbound') return;
        const purchaseLineId = String(load?.purchaseLineId || '');
        if (!purchaseLineId) return;
        const current = aggregated.get(purchaseLineId) || {
          purchaseLineId,
          quantity: 0,
          completedQuantity: 0,
          destinationNames: []
        };
        const loadQuantity = Math.max(1, Number(load?.quantity || 1) || 1);
        current.quantity += loadQuantity;
        if (String(destination?.status || '').trim().toLowerCase() === 'completed') {
          current.completedQuantity += loadQuantity;
        }
        if (destination?.name && !current.destinationNames.includes(destination.name)) {
          current.destinationNames.push(destination.name);
        }
        aggregated.set(purchaseLineId, current);
      });
    });

    return aggregated;
  }, [routeDestinations]);

  const validatedInboundByRouteLineId = useMemo(() => {
    const aggregated = new Map();

    stockMovements.forEach((movement) => {
      if (String(movement?.movementType || '').trim().toLowerCase() !== 'purchase_received') return;
      if (String(movement?.routeId || '') !== String(route?.id || '')) return;
      const purchaseLineId = String(movement?.purchaseLineId || '');
      if (!purchaseLineId) return;
      aggregated.set(
        purchaseLineId,
        Number(aggregated.get(purchaseLineId) || 0) + Math.max(0, Number(movement?.quantity || 0))
      );
    });

    return aggregated;
  }, [route?.id, stockMovements]);

  const inboundValidationItems = useMemo(() => {
    return Array.from(linkedPurchaseLoadsByLineId.values())
      .map((loadInfo) => {
        const purchaseLine = stockPurchaseLines.find((line) => String(line.id) === String(loadInfo.purchaseLineId));
        if (!purchaseLine) return null;
        const routeValidatedQty = Math.max(
          0,
          Number(validatedInboundByRouteLineId.get(String(loadInfo.purchaseLineId)) || 0)
        );
        const routeCompletedQty = Math.max(0, Number(loadInfo.completedQuantity || 0));
        const routePendingQty = Math.max(0, routeCompletedQty - routeValidatedQty);
        const transitQty = Math.max(
          0,
          Number(purchaseLine?.pickedUpQuantity || 0) - Number(purchaseLine?.receivedQuantity || 0)
        );
        const qtyToValidate = transitQty > 0
          ? Math.min(routePendingQty, transitQty)
          : routePendingQty;
        if (qtyToValidate <= 0) return null;

        return {
          purchaseLineId: loadInfo.purchaseLineId,
          purchaseId: String(purchaseLine?.purchaseId || ''),
          productId: String(purchaseLine?.productId || ''),
          productName: purchaseLine?.productNameSnapshot || 'Producto',
          sku: purchaseLine?.skuSnapshot || '',
          category: purchaseLine?.categorySnapshot || '',
          unit: purchaseLine?.unitSnapshot || 'unidad',
          quantity: qtyToValidate,
          routeQuantity: Math.max(0, Number(loadInfo.quantity || 0)),
          routeCompletedQuantity: routeCompletedQty,
          routeValidatedQuantity: routeValidatedQty,
          destinationNames: loadInfo.destinationNames,
          supplierName: purchaseLine?.supplierNameSnapshot || '',
          validationSource: transitQty > 0 ? 'stock_transit' : 'route_completion_fallback'
        };
      })
      .filter(Boolean)
      .sort((a, b) => String(a.productName || '').localeCompare(String(b.productName || ''), 'es', { sensitivity: 'base' }));
  }, [linkedPurchaseLoadsByLineId, stockPurchaseLines, validatedInboundByRouteLineId]);

  const routePendingInboundValidationCount = useMemo(() => {
    return Array.from(linkedPurchaseLoadsByLineId.values()).reduce((count, loadInfo) => {
      const routeCompletedQty = Math.max(0, Number(loadInfo?.completedQuantity || 0));
      const routeValidatedQty = Math.max(
        0,
        Number(validatedInboundByRouteLineId.get(String(loadInfo?.purchaseLineId || '')) || 0)
      );
      return routeCompletedQty > routeValidatedQty ? count + 1 : count;
    }, 0);
  }, [linkedPurchaseLoadsByLineId, validatedInboundByRouteLineId]);

  const shouldShowInboundValidationPanel =
    canManageRoute &&
    !hasPendingDestinations &&
    (
      inboundValidationItems.length > 0 ||
      routePendingInboundValidationCount > 0 ||
      Number(route?.pendingInboundValidationCount || 0) > 0 ||
      Boolean(route?.awaitingInboundValidation)
    );

  const routeDriverDisplayName = useMemo(
    () =>
      buildDriverDisplayName(
        {
          driverName: route?.driverName,
          driverLastName: route?.driverLastName,
          currentDriverName: route?.currentDriverName,
          currentDriverLastName: route?.currentDriverLastName,
          assignedDriverName: route?.assignedDriverName
        },
        'Sin chofer registrado'
      ),
    [route]
  );

  const routeDriverPhotoURL = useMemo(() => {
    const candidates = [
      route?.driverPhotoURL,
      route?.currentDriverPhotoURL,
      route?.photoURL,
      route?.transporter?.driverPhotoURL,
      route?.transporter?.currentDriverPhotoURL
    ];

    return candidates.find((value) => typeof value === 'string' && value.trim())?.trim() || '';
  }, [route]);

  const routeTransportDisplayName = useMemo(
    () =>
      buildTransportDisplayName(
        {
          transportName: route?.transportName,
          transporterName: route?.transporterName,
          nameOfTransport: route?.nameOfTransport,
          transportLabel: route?.transportLabel,
          transportModel: route?.transportModel,
          vehicleLabel: route?.vehicleLabel,
          patent: route?.patent,
          vehiclePatent: route?.vehiclePatent,
          transportType: route?.transportType,
          transporterSlug: route?.transporterSlug
        },
        'Sin unidad registrada'
      ),
    [route]
  );

  const routeTransportMeta = useMemo(
    () =>
      buildTransportMeta({
        transportName: route?.transportName,
        transporterName: route?.transporterName,
        transportModel: route?.transportModel,
        vehicleLabel: route?.vehicleLabel,
        patent: route?.patent,
        vehiclePatent: route?.vehiclePatent,
        transportType: route?.transportType
      }),
    [route]
  );

  const hasTransportProfileContext = Boolean(route?.transportId || route?.transporterId || route?.transporterSlug);

  const handleOpenTransportProfile = useCallback(() => {
    if (typeof showMessage === 'function') {
      showMessage(
        hasTransportProfileContext
          ? 'La ficha de esta unidad va a quedar disponible desde esta vista muy pronto.'
          : 'Esta ruta todavía no tiene una unidad vinculada para abrir su ficha.',
        hasTransportProfileContext ? 'info' : 'warning'
      );
    }
  }, [hasTransportProfileContext, showMessage]);

  const companyContactsPath = useMemo(
    () => resolveCompanyContactsPathSafe(resolvedCompanyId),
    [resolvedCompanyId]
  );

  const legacyContactsPath = useMemo(
    () => `artifacts/${appId}/public/data/contacts`,
    []
  );

  const linkColors = useMemo(
    () => generateLinkColors(routeDestinations),
    [routeDestinations]
  );

  useEffect(() => {
    setRoute(initialRoute || {});
  }, [initialRoute]);

  useEffect(() => {
    prevRouteRef.current = route;
  }, [route]);

  useEffect(() => {
    synth.current = new Tone.Synth().toDestination();

    return () => {
      if (synth.current?.dispose) {
        synth.current.dispose();
      }
    };
  }, []);

  useEffect(() => {
    if (!db || !appId) return;

    let companyDocs = [];
    let legacyDocs = [];
    let companyReady = false;
    let legacyReady = companyContactsPath === legacyContactsPath;
    let isCancelled = false;

    const normalizeSnapshotDocs = (snapshot, source) =>
      snapshot.docs.map((docSnap) => {
        const data = docSnap.data();

        return {
          id: docSnap.id,
          ...data,
          type: normalizeType(data.type),
          addresses: normalizeAddresses(data.addresses),
          contacts: normalizeContactPeople(data.contacts),
          _source: source
        };
      });

    const syncContacts = () => {
      if (!companyReady || !legacyReady || isCancelled) return;

      const merged = mergeContacts(companyDocs, legacyDocs);
      setAllContacts(merged);
    };

    const unsubscribeCompany = onSnapshot(
      query(collection(db, companyContactsPath)),
      (snapshot) => {
        companyReady = true;
        companyDocs = normalizeSnapshotDocs(snapshot, 'company');
        syncContacts();
      },
      (error) => {
        companyReady = true;
        companyDocs = [];
        console.error('Error cargando contactos de empresa en RouteDetail:', error);
        syncContacts();
      }
    );

    let unsubscribeLegacy = () => {};

    if (companyContactsPath !== legacyContactsPath) {
      unsubscribeLegacy = onSnapshot(
        query(collection(db, legacyContactsPath)),
        (snapshot) => {
          legacyReady = true;
          legacyDocs = normalizeSnapshotDocs(snapshot, 'legacy');
          syncContacts();
        },
        (error) => {
          legacyReady = true;
          legacyDocs = [];
          console.error('Error cargando contactos legacy en RouteDetail:', error);
          syncContacts();
        }
      );
    }

    return () => {
      isCancelled = true;
      unsubscribeCompany();
      unsubscribeLegacy();
    };
  }, [companyContactsPath, legacyContactsPath]);

  useEffect(() => {
    if (isTransporterView) {
      setStockPurchaseLines([]);
      return undefined;
    }

    const purchaseLinesRef = collection(db, resolveStockPurchaseLinesCollectionPath(resolvedCompanyId));

    const unsubscribe = onSnapshot(
      purchaseLinesRef,
      (snapshot) => {
        setStockPurchaseLines(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      (error) => {
        console.error('Error cargando líneas de compra en RouteDetail:', error);
        setStockPurchaseLines([]);
      }
    );

    return () => unsubscribe();
  }, [isTransporterView, resolvedCompanyId]);

  useEffect(() => {
    if (isTransporterView) {
      setStockMovements([]);
      return undefined;
    }

    const movementsRef = collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId));

    const unsubscribe = onSnapshot(
      movementsRef,
      (snapshot) => {
        setStockMovements(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      (error) => {
        console.error('Error cargando movimientos de stock en RouteDetail:', error);
        setStockMovements([]);
      }
    );

    return () => unsubscribe();
  }, [isTransporterView, resolvedCompanyId]);

  useEffect(() => {
    if (!resolvedCompanyId || !initialRoute?.id) {
      setRouteTraceEvents([]);
      return undefined;
    }

    const eventsRef = query(
      collection(db, resolveScanEventsCollectionPath(resolvedCompanyId)),
      where('routeId', '==', initialRoute.id)
    );

    const unsubscribe = onSnapshot(
      eventsRef,
      (snapshot) => {
        const events = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort((a, b) => getEventTimestampMillis(b.createdAt) - getEventTimestampMillis(a.createdAt));
        setRouteTraceEvents(events);
      },
      (error) => {
        console.error('Error cargando trazabilidad operativa de la ruta:', error);
        setRouteTraceEvents([]);
      }
    );

    return () => unsubscribe();
  }, [initialRoute?.id, resolvedCompanyId]);

  useEffect(() => {
    const mainRef = doc(db, resolveCompanyMainSettingsPath(resolvedCompanyId));
    const officeRef = doc(db, resolveCompanyOfficeSettingsPath(resolvedCompanyId));

    const unsubscribeMain = onSnapshot(
      mainRef,
      (docSnapshot) => {
        setCompanyMainSettings(docSnapshot.exists() ? docSnapshot.data() : null);
      },
      (error) => {
        console.error('Error cargando settings main en RouteDetail:', error);
        setCompanyMainSettings(null);
      }
    );

    const unsubscribeOffice = onSnapshot(
      officeRef,
      (docSnapshot) => {
        setCompanyOfficeSettings(docSnapshot.exists() ? docSnapshot.data() : null);
      },
      (error) => {
        console.error('Error cargando settings office en RouteDetail:', error);
        setCompanyOfficeSettings(null);
      }
    );

    return () => {
      unsubscribeMain();
      unsubscribeOffice();
    };
  }, [resolvedCompanyId]);

  useEffect(() => {
    if (!initialRoute?.id) return;

    const routeRef = buildLiveRouteDocumentRef(initialRoute, resolvedCompanyId);
    if (!routeRef) return;

    const unsubscribe = onSnapshot(routeRef, (docSnapshot) => {
      if (!docSnapshot.exists()) {
        if (typeof onBack === 'function') {
          onBack();
        }
        return;
      }

      const updatedRouteData = { id: docSnapshot.id, ...docSnapshot.data() };
      const previousRoute = prevRouteRef.current;
      const fallbackDestinations = [
        ...(Array.isArray(previousRoute?.destinations) ? previousRoute.destinations : []),
        ...(Array.isArray(initialRoute?.destinations) ? initialRoute.destinations : []),
      ];

      updatedRouteData.destinations = Array.isArray(updatedRouteData?.destinations)
        ? updatedRouteData.destinations.map((newDest) => {
            const previousDest =
              fallbackDestinations.find((destination) => destination?.id === newDest?.id) || null;

            return {
              ...(previousDest || {}),
              ...newDest,
              operationalLoads: Array.isArray(newDest?.operationalLoads)
                ? newDest.operationalLoads
                : Array.isArray(previousDest?.operationalLoads)
                  ? previousDest.operationalLoads
                  : [],
              incomingOperationalLoads: Array.isArray(newDest?.incomingOperationalLoads)
                ? newDest.incomingOperationalLoads
                : Array.isArray(previousDest?.incomingOperationalLoads)
                  ? previousDest.incomingOperationalLoads
                  : [],
            };
          })
        : fallbackDestinations;

      if (previousRoute?.destinations) {
        updatedRouteData.destinations?.forEach((newDest) => {
          const oldDest = previousRoute.destinations.find((d) => d.id === newDest.id);

          if (!oldDest) return;

          if (
            canManageRoute &&
            oldDest.status !== newDest.status &&
            ['completed', 'incomplete', 'cancelled', 'en_destino'].includes(newDest.status)
          ) {
            if (synth.current) {
              Tone.start().then(() => synth.current?.triggerAttackRelease('E5', '8n'));
            }

            setStatusNotification({ id: newDest.id, type: newDest.status });
            setTimeout(() => setStatusNotification({ id: null, type: '' }), 4000);
          }

          const newNotesCount = newDest.adminNotes?.length || 0;
          const oldNotesCount = oldDest.adminNotes?.length || 0;

          if (!canManageRoute && newNotesCount > oldNotesCount) {
            if (synth.current) {
              Tone.start().then(() => synth.current?.triggerAttackRelease('C5', '8n'));
            }
          }
        });
      }

      if (
        userRole === 'transportista' &&
        ['completed', 'cancelled'].includes(updatedRouteData.status || '') &&
        typeof onRouteClosed === 'function'
      ) {
        onRouteClosed(updatedRouteData);
        return;
      }

      setRoute(updatedRouteData);
    });

    return () => unsubscribe();
  }, [initialRoute?.id, initialRoute?._source, onBack, userRole, resolvedCompanyId]);

  useEffect(() => {
    const isFreshRoute =
      route?.status === 'in_progress' &&
      !routeDestinations.some((d) => d.status !== 'pending');

    if (userRole !== 'transportista') {
      setShowStartTripModal(false);
      return;
    }

    if (isFreshRoute) {
      setShowStartTripModal(true);
    }
  }, [route?.id, route?.status, routeDestinations, userRole]);

  useEffect(() => {
    const currentDestinations = routeDestinations;
    const previousDestinations = previousDestinationsRef.current || [];

    if (userRole !== 'transportista') {
      previousDestinationsRef.current = currentDestinations;
      return;
    }

    if (previousDestinations.length === 0) {
      previousDestinationsRef.current = currentDestinations;
      return;
    }

    const resolvedStatuses = ['completed', 'incomplete', 'cancelled'];

    const justResolvedDestination = currentDestinations.find((currentDest) => {
      const previousDest = previousDestinations.find((d) => d.id === currentDest.id);
      if (!previousDest) return false;

      return (
        previousDest.status !== currentDest.status &&
        resolvedStatuses.includes(currentDest.status)
      );
    });

    if (justResolvedDestination) {
      const pendingDestinations = currentDestinations.filter((d) => d.status === 'pending');

      if (pendingDestinations.length > 0) {
        setShowNextDestinationModal(true);
      }
    }

    previousDestinationsRef.current = currentDestinations;
  }, [routeDestinations, userRole]);


  useEffect(() => {
    if (!canManageRoute || !route?.id || validRouteDestinationsCount === 0) return;

    const currentLegsCount = Object.keys(routeRoadMetrics?.legsByDestinationId || {}).length;
    const signatureChanged = routeRoadMetrics?.destinationSignature !== routeRoadSignature;
    const missingFullGeometry = !Array.isArray(routeRoadMetrics?.geometry) || routeRoadMetrics.geometry.length < 2;
    const missingLegGeometry = validRouteDestinations.some((dest) => {
      const legGeometry = routeRoadMetrics?.legsByDestinationId?.[dest.id]?.geometry;
      return !Array.isArray(legGeometry) || legGeometry.length < 2;
    });
    const shouldHydrateRoadMetrics = !routeRoadMetrics || currentLegsCount !== validRouteDestinationsCount || signatureChanged || missingFullGeometry || missingLegGeometry;

    if (!shouldHydrateRoadMetrics || roadMetricsHydrationRef.current) return;

    roadMetricsHydrationRef.current = true;

    buildRouteRoadMetrics({
      route,
      officeLocation: resolvedOfficeLocation,
      completedOnly: false
    })
      .then(async (roadMetrics) => {
        if (!roadMetrics) return;

        await syncRouteDocuments({
          roadMetrics,
          estimatedDistanceKm: Number(roadMetrics.totalDistanceKm || 0),
          estimatedDurationMin: Number(roadMetrics.totalDurationMin || 0),
          distanceSource: roadMetrics.source || 'road'
        });
      })
      .catch((error) => {
        console.error('Error generando métricas viales de la ruta:', error);
      })
      .finally(() => {
        roadMetricsHydrationRef.current = false;
      });
  }, [canManageRoute, route, resolvedCompanyId, resolvedOfficeLocation, routeRoadMetrics, routeRoadSignature, validRouteDestinationsCount, syncRouteDocuments]);

  const getCompletedAtMillis = (value) => {
    if (!value) return 0;
    if (typeof value?.toDate === 'function') return value.toDate().getTime();
    if (value instanceof Date) return value.getTime();
    if (typeof value?.seconds === 'number') return value.seconds * 1000;
    return 0;
  };

  const traceEventsByDestinationId = useMemo(() => {
    const grouped = new Map();
    routeTraceEvents.forEach((event) => {
      const destinationId = String(event?.destinationId || '').trim();
      if (!destinationId) return;
      const current = grouped.get(destinationId) || [];
      current.push(event);
      grouped.set(destinationId, current);
    });
    return grouped;
  }, [routeTraceEvents]);

  const sortedDestinations = useMemo(() => {
    if (routeDestinations.length === 0) return [];

    const completedOrResolved = routeDestinations
      .filter((d) => ['completed', 'incomplete', 'cancelled'].includes(d.status))
      .sort((a, b) => getCompletedAtMillis(a.completedAt) - getCompletedAtMillis(b.completedAt));

    const enCamino = routeDestinations.filter((d) => d.status === 'en_camino');
    const enDestino = routeDestinations.filter((d) => d.status === 'en_destino');
    const pending = routeDestinations
      .filter((d) => d.status === 'pending')
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    return [...completedOrResolved, ...enCamino, ...enDestino, ...pending];
  }, [routeDestinations]);

  const resolveIncomingOperationalLoadLabel = useCallback(
    (incoming = {}) => {
      const directLabel = String(
        incoming?.title ||
          incoming?.productNameSnapshot ||
          incoming?.stockProductNameSnapshot ||
          incoming?.productName ||
          ''
      ).trim();
      if (directLabel) return directLabel;

      const sourceDestination = routeDestinations.find(
        (destination) => String(destination?.id || '') === String(incoming?.sourceDestinationId || '')
      );
      const sourceLoad = (sourceDestination?.operationalLoads || []).find(
        (load) => String(load?.id || '') === String(incoming?.loadId || '')
      );

      return (
        String(
          sourceLoad?.title ||
            sourceLoad?.productNameSnapshot ||
            sourceLoad?.stockProductNameSnapshot ||
            sourceLoad?.productName ||
            ''
        ).trim() || 'Producto'
      );
    },
    [routeDestinations]
  );

  useEffect(() => {
    setCollapsedDestinationIds((previous) => {
      const next = new Set();
      routeDestinations.forEach((destination) => {
        if (previous.has(destination.id)) next.add(destination.id);
      });
      return next;
    });
  }, [routeDestinations]);

  const toggleDestinationCollapsed = useCallback((destinationId) => {
    setCollapsedDestinationIds((previous) => {
      const next = new Set(previous);
      if (next.has(destinationId)) {
        next.delete(destinationId);
      } else {
        next.add(destinationId);
      }
      return next;
    });
  }, []);

  const focusDestinationFromMiniPill = useCallback((destinationId) => {
    setCollapsedDestinationIds((previous) => {
      const next = new Set(previous);
      next.delete(destinationId);
      return next;
    });

    window.setTimeout(() => {
      const containerNode = destinationListContainerRef.current;
      const targetNode = destinationItemRefs.current?.[destinationId];
      if (containerNode && targetNode) {
        const containerRect = containerNode.getBoundingClientRect();
        const targetRect = targetNode.getBoundingClientRect();
        const nextTop =
          containerNode.scrollTop + (targetRect.top - containerRect.top) - 12;

        containerNode.scrollTo({
          top: Math.max(0, nextTop),
          behavior: 'smooth'
        });
      }
    }, 120);
  }, []);

  const toggleGeneralListView = useCallback(() => {
    setIsGeneralListView((previous) => {
      const nextValue = !previous;
      setCollapsedDestinationIds(
        nextValue ? new Set(routeDestinations.map((destination) => destination.id)) : new Set()
      );
      return nextValue;
    });
  }, [routeDestinations]);

  useEffect(() => {
    if (!route?.id || routeDestinations.length === 0) return;
    if (autoCollapsedRouteIdRef.current === route.id) return;

    autoCollapsedRouteIdRef.current = route.id;

    setIsGeneralListView(false);
    setCollapsedDestinationIds(new Set());

    if (autoCollapseDestinationsTimeoutRef.current) {
      clearTimeout(autoCollapseDestinationsTimeoutRef.current);
    }

    autoCollapseDestinationsTimeoutRef.current = setTimeout(() => {
      setCollapsedDestinationIds(new Set(routeDestinations.map((destination) => destination.id)));
      setIsGeneralListView(true);
      autoCollapseDestinationsTimeoutRef.current = null;
    }, 3000);
  }, [route?.id, routeDestinations]);

  useEffect(() => {
    return () => {
      if (autoCollapseDestinationsTimeoutRef.current) {
        clearTimeout(autoCollapseDestinationsTimeoutRef.current);
        autoCollapseDestinationsTimeoutRef.current = null;
      }
      autoCollapsedRouteIdRef.current = null;
    };
  }, [route?.id]);

  const lastCompletedDestination = useMemo(() => {
    const completedOrResolved = routeDestinations
      .filter((d) => ['completed', 'incomplete', 'cancelled'].includes(d.status))
      .sort((a, b) => getCompletedAtMillis(a.completedAt) - getCompletedAtMillis(b.completedAt));

    return completedOrResolved.length > 0
      ? completedOrResolved[completedOrResolved.length - 1]
      : null;
  }, [routeDestinations]);

  const handleUpdateDestination = useCallback(
    async (updatedDestination, showSuccessMessage = true) => {
      let completedAtTimestamp = updatedDestination.completedAt || null;

      if (['completed', 'incomplete', 'cancelled'].includes(updatedDestination.status)) {
        if (!completedAtTimestamp) {
          completedAtTimestamp = new Date();
        }
      }

      if (completedAtTimestamp && typeof completedAtTimestamp.toDate === 'function') {
        completedAtTimestamp = completedAtTimestamp.toDate();
      }

      const previousDestination = routeDestinations.find((d) => d.id === updatedDestination.id) || null;
      const destinationWithTime = {
        ...(previousDestination || {}),
        ...updatedDestination,
        operationalLoads: Array.isArray(updatedDestination?.operationalLoads)
          ? updatedDestination.operationalLoads
          : Array.isArray(previousDestination?.operationalLoads)
            ? previousDestination.operationalLoads
            : [],
        incomingOperationalLoads: Array.isArray(updatedDestination?.incomingOperationalLoads)
          ? updatedDestination.incomingOperationalLoads
          : Array.isArray(previousDestination?.incomingOperationalLoads)
            ? previousDestination.incomingOperationalLoads
            : [],
        completedAt: completedAtTimestamp
      };

      const newDestinations = routeDestinations.map((d) =>
        d.id === destinationWithTime.id ? destinationWithTime : d
      );
      const computedRouteStatus = getRouteStatus({
        ...route,
        destinations: newDestinations
      });
      const pendingDestinationsAfterUpdate = newDestinations.filter((d) => d.status === 'pending');
      const nextPendingInboundValidationCount = newDestinations.reduce((count, destination) => {
        if (String(destination?.status || '').trim().toLowerCase() !== 'completed') return count;

        const hasPendingInboundLoad = (destination?.operationalLoads || []).some(
          (load) =>
            load?.purchaseLineId &&
            String(load?.stockMode || '').trim().toLowerCase() === 'pending_inbound'
        );

        return hasPendingInboundLoad ? count + 1 : count;
      }, 0);
      const currentRouteStatus = String(route?.status || '').trim().toLowerCase();
      const shouldKeepRouteOpen =
        ['completed', 'incomplete', 'cancelled'].includes(computedRouteStatus) &&
        !['completed', 'cancelled'].includes(currentRouteStatus);
      const nextRouteStatus = shouldKeepRouteOpen
        ? (currentRouteStatus || 'in_progress')
        : computedRouteStatus;
      const optimisticRouteSnapshot = {
        ...route,
        destinations: newDestinations,
        status: nextRouteStatus,
        pendingInboundValidationCount: nextPendingInboundValidationCount,
        awaitingInboundValidation: nextPendingInboundValidationCount > 0
      };
      const runDestinationSideEffects = async () => {
        const shouldMoveInboundToTransit =
          previousDestination?.status !== 'completed' &&
          destinationWithTime.status === 'completed' &&
          (destinationWithTime.operationalLoads || []).some(
            (load) =>
              load?.purchaseLineId &&
              String(load?.stockMode || '').trim().toLowerCase() === 'pending_inbound'
          );

        if (shouldMoveInboundToTransit) {
          try {
            await handleMarkInboundLoadsAsPickedUp(destinationWithTime);
          } catch (movementError) {
            console.error('Destino guardado, pero falló la sincronización de ingreso con stock:', movementError);
            if (userRole !== 'transportista') throw movementError;
            showMessage('Destino guardado. Quedó pendiente sincronizar ingreso de stock.', 'warning');
          }
        }

        const shouldCompleteOutboundLoads =
          previousDestination?.status !== 'completed' &&
          destinationWithTime.status === 'completed' &&
          (destinationWithTime.operationalLoads || []).some(
            (load) => load?.stockProductId && load?.stockMode === 'pending_outbound'
          );

        if (shouldCompleteOutboundLoads) {
          try {
            await handleCompleteOutboundLoads(destinationWithTime, previousDestination);
          } catch (movementError) {
            console.error('Destino guardado, pero falló la sincronización de salida con stock:', movementError);
            if (userRole !== 'transportista') throw movementError;
            showMessage('Destino guardado. Quedó pendiente sincronizar salida de stock.', 'warning');
          }
        }

        const shouldReturnOutboundLoadsToPending =
          !['completed', 'incomplete', 'cancelled'].includes(String(previousDestination?.status || '')) &&
          ['incomplete', 'cancelled'].includes(destinationWithTime.status) &&
          (destinationWithTime.operationalLoads || []).some(
            (load) => load?.stockProductId && load?.stockMode === 'pending_outbound'
          );

        if (shouldReturnOutboundLoadsToPending) {
          try {
            await handleReturnOutboundLoadsToPending(destinationWithTime, previousDestination, {
              reason: destinationWithTime.status === 'cancelled' ? 'cancelled' : 'replanned'
            });
          } catch (movementError) {
            console.error('Destino guardado, pero falló la devolución de cargas a pendiente:', movementError);
            if (userRole !== 'transportista') throw movementError;
            showMessage('Destino guardado. Quedó pendiente sincronizar devolución de stock.', 'warning');
          }
        }

        const shouldSyncLinkedService =
          previousDestination?.status !== destinationWithTime.status &&
          Boolean(destinationWithTime?.serviceId) &&
          ['completed', 'cancelled'].includes(String(destinationWithTime.status || '').trim().toLowerCase());

        if (shouldSyncLinkedService) {
          try {
            await syncLinkedServiceExecution(destinationWithTime, newDestinations);
          } catch (serviceError) {
            console.error('Destino guardado, pero falló la sincronización del servicio comercial:', serviceError);
            if (userRole !== 'transportista') throw serviceError;
            showMessage('Destino guardado. Quedó pendiente cerrar el servicio comercial.', 'warning');
          }
        }
      };

      try {
        if (userRole === 'transportista') {
          setRoute((prev) => ({
            ...(prev || {}),
            ...optimisticRouteSnapshot
          }));
        }

        await syncRouteDocuments({
          destinations: newDestinations,
          status: nextRouteStatus,
          pendingInboundValidationCount: nextPendingInboundValidationCount,
          awaitingInboundValidation: nextPendingInboundValidationCount > 0
        });

        if (
          userRole === 'transportista' &&
          ['completed', 'incomplete', 'cancelled'].includes(destinationWithTime.status) &&
          pendingDestinationsAfterUpdate.length > 0
        ) {
          setShowNextDestinationModal(true);
        }

        if (showSuccessMessage) {
          if (shouldKeepRouteOpen && pendingDestinationsAfterUpdate.length === 0) {
            showMessage('Destino actualizado. Todos los destinos quedaron resueltos; falta finalizar la ruta.', 'success');
          } else {
            showMessage('Destino actualizado.', 'success');
          }

          if (
            updatedDestination.status !== 'pending' &&
            typeof addLogisticsNotification === 'function'
          ) {
            addLogisticsNotification(
              `Destino "${updatedDestination.name}" marcado como ${updatedDestination.status}.`
            );
          }
        }

        if (userRole === 'transportista') {
          if (showSuccessMessage) {
            const requiresFollowUpSync =
              ['completed', 'incomplete', 'cancelled'].includes(String(destinationWithTime.status || '').trim().toLowerCase()) &&
              (
                (destinationWithTime.operationalLoads || []).some((load) => load?.purchaseLineId || load?.stockProductId) ||
                Boolean(destinationWithTime?.serviceId)
              );

            if (requiresFollowUpSync) {
              showMessage('Destino guardado. Seguimos sincronizando stock y validaciones en segundo plano.', 'info');
            }
          }

          Promise.resolve(runDestinationSideEffects()).catch((backgroundError) => {
            console.error('Error ejecutando sincronización diferida del destino:', backgroundError);
          });
        } else {
          await runDestinationSideEffects();
        }
      } catch (error) {
        if (userRole === 'transportista') {
          setRoute((prev) => ({
            ...(prev || {}),
            destinations: routeDestinations,
            status: route?.status,
            pendingInboundValidationCount: route?.pendingInboundValidationCount || 0,
            awaitingInboundValidation: Boolean(route?.awaitingInboundValidation)
          }));
        }
        console.error('Error al actualizar destino:', error, updatedDestination);
        showMessage(`Error al actualizar: ${error.message}`, 'error');
      }
    },
    [
      route,
      routeDestinations,
      showMessage,
      addLogisticsNotification,
      resolvedCompanyId,
      userRole
    ]
  );


  async function handleMarkInboundLoadsAsPickedUp(destinationWithTime) {
      const destinationLoads = (destinationWithTime?.operationalLoads || []).filter(
        (load) =>
          load?.purchaseLineId &&
          String(load?.stockMode || '').trim().toLowerCase() === 'pending_inbound'
      );
      if (!destinationLoads.length) return;

      const groupedLoads = destinationLoads.reduce((acc, load) => {
        const purchaseLineId = String(load?.purchaseLineId || '');
        if (!purchaseLineId) return acc;
        acc[purchaseLineId] = (acc[purchaseLineId] || 0) + Math.max(1, Number(load?.quantity || 1) || 1);
        return acc;
      }, {});

      const affectedPurchaseIds = new Set();
      const nextLinesById = new Map();
      const batch = writeBatch(db);
      const now = new Date();
      const purchaseLineCache = new Map(
        (stockPurchaseLines || []).map((line) => [String(line?.id || ''), line])
      );
      const missingPurchaseLineIds = Object.keys(groupedLoads).filter(
        (purchaseLineId) => purchaseLineId && !purchaseLineCache.has(String(purchaseLineId))
      );

      if (missingPurchaseLineIds.length > 0) {
        await Promise.all(
          missingPurchaseLineIds.map(async (purchaseLineId) => {
            try {
              const lineSnap = await getDoc(
                doc(db, resolveStockPurchaseLineDocPath(resolvedCompanyId, purchaseLineId))
              );
              if (lineSnap.exists()) {
                purchaseLineCache.set(String(lineSnap.id), {
                  id: lineSnap.id,
                  ...lineSnap.data(),
                });
              }
            } catch (error) {
              console.error('Error cargando línea de compra para retiro en curso:', purchaseLineId, error);
            }
          })
        );
      }

      const unresolvedPurchaseLineIds = [];

      Object.entries(groupedLoads).forEach(([purchaseLineId, movedQty]) => {
        const currentLine = purchaseLineCache.get(String(purchaseLineId)) || null;
        if (!currentLine) {
          unresolvedPurchaseLineIds.push(String(purchaseLineId));
          return;
        }
        affectedPurchaseIds.add(String(currentLine.purchaseId || ''));

        const nextPickedUp = Number(currentLine.pickedUpQuantity || 0) + movedQty;
        const nextReceived = Number(currentLine.receivedQuantity || 0);
        const nextTransit = Math.max(0, nextPickedUp - nextReceived);
        const nextStatus = nextTransit > 0 ? 'in_transit' : currentLine.status || 'assigned_to_route';
        const movementRef = doc(collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId)));
        const nextLine = {
          ...currentLine,
          pickedUpQuantity: nextPickedUp,
          status: nextStatus,
          lastRouteId: route?.id || currentLine.lastRouteId || null,
          lastRouteCodeSnapshot: route?.name || currentLine.lastRouteCodeSnapshot || '',
          lastMovementId: movementRef.id,
          lastMovementStatus: 'applied',
          updatedAt: now
        };
        nextLinesById.set(String(currentLine.id), nextLine);

        batch.update(doc(db, resolveStockPurchaseLineDocPath(resolvedCompanyId, purchaseLineId)), {
          pickedUpQuantity: nextPickedUp,
          status: nextStatus,
          lastRouteId: route?.id || currentLine.lastRouteId || null,
          lastRouteCodeSnapshot: route?.name || currentLine.lastRouteCodeSnapshot || '',
          lastMovementId: movementRef.id,
          lastMovementStatus: 'applied',
          updatedAt: now
        });

        if (currentLine.productId) {
          batch.update(doc(db, resolveStockProductDocPath(resolvedCompanyId, currentLine.productId)), {
            'stockBuckets.pendingInboundQty': increment(-movedQty),
            'stockBuckets.inboundTransitQty': increment(movedQty),
            'stockSummary.lastMovementAt': now,
            'stockSummary.lastMovementType': 'purchase_pickup_started',
            'stockSummary.lastOperationReason': null,
            'stockSummary.lastOperationType': 'purchase',
            'stockSummary.lastRouteCodeSnapshot': route?.name || '',
            'stockSummary.lastDestinationNameSnapshot': destinationWithTime?.name || '',
            updatedAt: now
          });
        }

        batch.set(movementRef, {
          companyId: resolvedCompanyId,
          productId: currentLine.productId || '',
          productNameSnapshot: currentLine.productNameSnapshot || '',
          skuSnapshot: currentLine.skuSnapshot || '',
          categorySnapshot: currentLine.categorySnapshot || '',
          unitSnapshot: currentLine.unitSnapshot || 'unidad',
          movementType: 'purchase_pickup_started',
          quantity: movedQty,
          stockBucketsImpact: {
            pendingInboundDelta: -movedQty,
            inboundTransitDelta: movedQty,
            physicalDelta: 0,
            pendingOutboundDelta: 0,
            outboundTransitDelta: 0,
            repairDelta: 0,
            returnedDelta: 0,
            damagedDelta: 0
          },
          status: 'applied',
          sourceType: 'route',
          sourceId: route?.id || '',
          sourceLineId: purchaseLineId,
          supplierId: currentLine.supplierId || '',
          supplierNameSnapshot: currentLine.supplierNameSnapshot || '',
          routeId: route?.id || '',
          routeCodeSnapshot: route?.name || '',
            transportId: route?.transporterId || route?.transportId || '',
            transportNameSnapshot: route?.transporterName || route?.transportName || route?.vehicleLabel || '',
            driverId: route?.driverId || '',
            driverNameSnapshot: [route?.driverName, route?.driverLastName].filter(Boolean).join(' ').trim(),
            driverPhotoURLSnapshot: route?.driverPhotoURL || route?.currentDriverPhotoURL || route?.photoURL || '',
            destinationId: destinationWithTime?.id || '',
          destinationNameSnapshot: destinationWithTime?.name || '',
          note: 'Retiro completado por el conductor. La mercadería pasó a tránsito de ingreso.',
          createdByUserId: userRole || 'transportista',
          createdByUserName: userRole || 'transportista',
          createdAt: now,
          appliedAt: now,
          cancelledAt: null
        });
      });

      if (unresolvedPurchaseLineIds.length > 0) {
        throw new Error(`missing-purchase-lines:${unresolvedPurchaseLineIds.join(',')}`);
      }

      affectedPurchaseIds.forEach((purchaseId) => {
        if (!purchaseId) return;
        const purchaseLinesForPurchase = Array.from(purchaseLineCache.values())
          .filter((line) => String(line.purchaseId || '') === purchaseId)
          .map((line) => nextLinesById.get(String(line.id)) || line);
        const purchaseSummary = buildPurchaseHeaderSummary({ id: purchaseId }, purchaseLinesForPurchase);
        batch.update(doc(db, resolveStockPurchaseDocPath(resolvedCompanyId, purchaseId)), {
          status: purchaseSummary.status,
          updatedAt: now,
          completedLinesCount: purchaseSummary.completedLinesCount,
          pendingLinesCount: purchaseSummary.pendingLinesCount,
          closedAt: purchaseSummary.closedAt
        });
      });

      const routeCompanyDocRef = buildCompanyRouteDocumentRef(route, resolvedCompanyId);
      if (routeCompanyDocRef) {
        batch.update(routeCompanyDocRef, {
          pendingInboundValidationCount: increment(Object.keys(groupedLoads).length),
          awaitingInboundValidation: true,
          updatedAt: now
        });
      }

      await batch.commit();
  }

  async function handleMoveOutboundLoadsToTransit(destinationWithTime) {
      const destinationLoads = (destinationWithTime?.operationalLoads || []).filter(
        (load) => load?.stockProductId && load?.stockMode === 'pending_outbound'
      );
      if (!destinationLoads.length) return;

      const groupedLoads = destinationLoads.reduce((acc, load) => {
        const productId = String(load?.stockProductId || '');
        if (!productId) return acc;
        const quantity = Math.max(1, Number(load?.quantity || 1) || 1);
        if (!acc[productId]) {
          acc[productId] = {
            quantity: 0,
            sampleLoad: load
          };
        }
        acc[productId].quantity += quantity;
        return acc;
      }, {});

      const batch = writeBatch(db);
      const now = new Date();

      Object.entries(groupedLoads).forEach(([productId, grouped]) => {
        const load = grouped.sampleLoad || {};
        const movedQty = Math.max(0, Number(grouped.quantity || 0));
        if (!movedQty) return;

        const movementRef = doc(collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId)));

        batch.update(doc(db, resolveStockProductDocPath(resolvedCompanyId, productId)), {
          'stockBuckets.pendingOutboundQty': increment(-movedQty),
          'stockBuckets.outboundTransitQty': increment(movedQty),
          'stockBuckets.physicalQty': increment(-movedQty),
          'stockSummary.lastMovementAt': now,
          'stockSummary.lastMovementType': 'stock_outbound_started',
          'stockSummary.lastOperationReason': String(load.stockOperationReason || 'sale').trim().toLowerCase() || 'sale',
          'stockSummary.lastOperationType': String(load.operationType || '').trim().toLowerCase(),
          'stockSummary.lastRouteCodeSnapshot': route?.name || '',
          'stockSummary.lastDestinationNameSnapshot': destinationWithTime?.name || '',
          updatedAt: now
        });

        batch.set(movementRef, {
          companyId: resolvedCompanyId,
          productId,
          productNameSnapshot: load.stockProductNameSnapshot || load.title || '',
          skuSnapshot: load.stockSkuSnapshot || '',
          categorySnapshot: load.stockCategorySnapshot || load.category || '',
          unitSnapshot: load.stockUnitSnapshot || load.unit || 'unidad',
          movementType: 'stock_outbound_started',
          quantity: movedQty,
          stockBucketsImpact: {
            pendingInboundDelta: 0,
            inboundTransitDelta: 0,
            physicalDelta: -movedQty,
            pendingOutboundDelta: -movedQty,
            outboundTransitDelta: movedQty,
            repairDelta: 0,
            returnedDelta: 0,
            damagedDelta: 0
          },
          status: 'applied',
          sourceType: 'route',
          sourceId: route?.id || '',
          sourceLineId: String(load.id || ''),
          stockOutboundOrderId: String(load.stockOutboundOrderId || ''),
          stockOutboundOrderNumber: String(load.stockOutboundOrderNumber || ''),
          stockOutboundOrderLineId: String(load.stockOutboundOrderLineId || ''),
          purchaseId: String(load.purchaseId || ''),
          purchaseLineId: String(load.purchaseLineId || ''),
          purchaseNumberSnapshot: String(load.purchaseNumber || ''),
          batchLabelSnapshot: String(load.batchLabelSnapshot || ''),
          routeId: route?.id || '',
          routeCodeSnapshot: route?.name || '',
            transportId: route?.transporterId || route?.transportId || '',
            transportNameSnapshot: route?.transporterName || route?.transportName || route?.vehicleLabel || '',
            driverId: route?.driverId || '',
            driverNameSnapshot: [route?.driverName, route?.driverLastName].filter(Boolean).join(' ').trim(),
            driverPhotoURLSnapshot: route?.driverPhotoURL || route?.currentDriverPhotoURL || route?.photoURL || '',
            destinationId: destinationWithTime?.id || '',
          destinationNameSnapshot: destinationWithTime?.name || '',
          customerId: destinationWithTime?.contactId || '',
          customerNameSnapshot: destinationWithTime?.contactName || destinationWithTime?.name || '',
          operationType: String(load.operationType || '').trim().toLowerCase(),
          operationReason: String(load.stockOperationReason || 'sale').trim().toLowerCase() || 'sale',
          referenceCodeSnapshot: String(load.referenceCode || '').trim(),
          note: 'La salida operativa quedó en camino y ya impacta como tránsito de salida.',
          createdByUserId: userRole || 'transportista',
          createdByUserName: userRole || 'transportista',
          createdAt: now,
          appliedAt: now,
          cancelledAt: null
        });
      });

      const groupedByOutboundOrder = destinationLoads.reduce((acc, load) => {
        const orderId = String(load?.stockOutboundOrderId || '');
        const lineId = String(load?.stockOutboundOrderLineId || '');
        if (!orderId || !lineId) return acc;
        if (!acc[orderId]) acc[orderId] = {};
        acc[orderId][lineId] = (acc[orderId][lineId] || 0) + Math.max(1, Number(load?.quantity || 1) || 1);
        return acc;
      }, {});

      for (const [orderId, lineMap] of Object.entries(groupedByOutboundOrder)) {
        const orderRef = doc(db, resolveStockOutboundOrderDocPath(resolvedCompanyId, orderId));
        const orderSnap = await getDoc(orderRef);
        if (!orderSnap.exists()) continue;
        const currentOrder = { id: orderSnap.id, ...orderSnap.data() };
        const nextLines = (currentOrder.lines || []).map((line) => {
          const movedQty = Number(lineMap[String(line.id)] || 0);
          if (!movedQty) return line;
          const nextAssigned = Math.max(0, Number(line.assignedQuantity || 0) - movedQty);
          const nextInTransit = Number(line.inTransitQuantity || 0) + movedQty;
          return {
            ...line,
            assignedQuantity: nextAssigned,
            inTransitQuantity: nextInTransit,
            status: 'in_transit',
            updatedAt: now,
          };
        });
        const summary = buildOutboundOrderHeaderSummary(currentOrder, nextLines);
        batch.update(orderRef, {
          lines: nextLines,
          status: summary.status,
          pendingDeliveryQuantity: summary.totals.pendingDeliveryQuantity,
          assignedQuantity: summary.totals.assignedQuantity,
          inTransitQuantity: summary.totals.inTransitQuantity,
          deliveredQuantity: summary.totals.deliveredQuantity,
          updatedAt: now,
          closedAt: summary.closedAt,
        });
      }

      await batch.commit();
  }

  async function handleCompleteOutboundLoads(destinationWithTime, previousDestination = null) {
      const destinationLoads = (destinationWithTime?.operationalLoads || []).filter(
        (load) => load?.stockProductId && load?.stockMode === 'pending_outbound'
      );
      if (!destinationLoads.length) return;

      const startedPreviously = ['en_camino', 'en_destino'].includes(String(previousDestination?.status || ''));
      const groupedLoads = destinationLoads.reduce((acc, load) => {
        const productId = String(load?.stockProductId || '');
        if (!productId) return acc;
        const quantity = Math.max(1, Number(load?.quantity || 1) || 1);
        if (!acc[productId]) {
          acc[productId] = {
            quantity: 0,
            sampleLoad: load
          };
        }
        acc[productId].quantity += quantity;
        return acc;
      }, {});

      const batch = writeBatch(db);
      const now = new Date();

      Object.entries(groupedLoads).forEach(([productId, grouped]) => {
        const load = grouped.sampleLoad || {};
        const deliveredQty = Math.max(0, Number(grouped.quantity || 0));
        if (!deliveredQty) return;
        const normalizedReason = String(load.stockOperationReason || 'sale').trim().toLowerCase() || 'sale';
        const isWarrantyFlow = ['warranty', 'guarantee', 'repair'].includes(normalizedReason);

        const movementRef = doc(collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId)));
        const productRef = doc(db, resolveStockProductDocPath(resolvedCompanyId, productId));
        const productUpdate = {
          'stockSummary.lastMovementAt': now,
          'stockSummary.lastMovementType': 'stock_outbound_delivered',
          'stockSummary.lastMovementStatus': 'applied',
          'stockSummary.lastOperationReason': normalizedReason,
          'stockSummary.lastOperationType': String(load.operationType || '').trim().toLowerCase(),
          'stockSummary.lastRouteCodeSnapshot': route?.name || '',
          'stockSummary.lastDestinationNameSnapshot': destinationWithTime?.name || '',
          updatedAt: now
        };

        if (startedPreviously) {
          productUpdate['stockBuckets.outboundTransitQty'] = increment(-deliveredQty);
        } else {
          productUpdate['stockBuckets.pendingOutboundQty'] = increment(-deliveredQty);
          productUpdate['stockBuckets.physicalQty'] = increment(-deliveredQty);
        }
        if (isWarrantyFlow) {
          productUpdate['stockBuckets.repairQty'] = increment(deliveredQty);
          productUpdate['stockSummary.specialState'] = 'in_warranty';
          productUpdate['stockSummary.specialStateQty'] = deliveredQty;
          productUpdate['stockSummary.warrantyEnteredAt'] = now;
          productUpdate['stockSummary.warrantyPickupApprovedAt'] = null;
          productUpdate['stockSummary.warrantyOutcome'] = null;
        }

        batch.update(productRef, productUpdate);

        batch.set(movementRef, {
          companyId: resolvedCompanyId,
          productId,
          productNameSnapshot: load.stockProductNameSnapshot || load.title || '',
          skuSnapshot: load.stockSkuSnapshot || '',
          categorySnapshot: load.stockCategorySnapshot || load.category || '',
          unitSnapshot: load.stockUnitSnapshot || load.unit || 'unidad',
          movementType: 'stock_outbound_delivered',
          quantity: deliveredQty,
          stockBucketsImpact: {
            pendingInboundDelta: 0,
            inboundTransitDelta: 0,
            physicalDelta: startedPreviously ? 0 : -deliveredQty,
            pendingOutboundDelta: startedPreviously ? 0 : -deliveredQty,
            outboundTransitDelta: startedPreviously ? -deliveredQty : 0,
            repairDelta: isWarrantyFlow ? deliveredQty : 0,
            returnedDelta: 0,
            damagedDelta: 0
          },
          status: 'applied',
          sourceType: 'route',
          sourceId: route?.id || '',
          sourceLineId: String(load.id || ''),
          purchaseId: String(load.purchaseId || ''),
          purchaseLineId: String(load.purchaseLineId || ''),
          purchaseNumberSnapshot: String(load.purchaseNumber || ''),
          batchLabelSnapshot:
            load.purchaseNumber && load.purchaseLineId
              ? `Lote ${String(load.purchaseNumber).slice(0, 6).toUpperCase()}`
              : '',
          routeId: route?.id || '',
          routeCodeSnapshot: route?.name || '',
            transportId: route?.transporterId || route?.transportId || '',
            transportNameSnapshot: route?.transporterName || route?.transportName || route?.vehicleLabel || '',
            driverId: route?.driverId || '',
            driverNameSnapshot: [route?.driverName, route?.driverLastName].filter(Boolean).join(' ').trim(),
            driverPhotoURLSnapshot: route?.driverPhotoURL || route?.currentDriverPhotoURL || route?.photoURL || '',
            destinationId: destinationWithTime?.id || '',
          destinationNameSnapshot: destinationWithTime?.name || '',
          customerId: destinationWithTime?.contactId || '',
          customerNameSnapshot: destinationWithTime?.contactName || destinationWithTime?.name || '',
          operationType: String(load.operationType || '').trim().toLowerCase(),
          operationReason: normalizedReason,
          referenceCodeSnapshot: String(load.referenceCode || '').trim(),
          costUnitSnapshot:
            load.costUnitSnapshot == null || load.costUnitSnapshot === ''
              ? null
              : Number(load.costUnitSnapshot),
          costTotalSnapshot:
            load.costTotalSnapshot == null || load.costTotalSnapshot === ''
              ? null
              : Number(load.costTotalSnapshot),
          saleUnitSnapshot:
            load.saleUnitPrice == null || load.saleUnitPrice === ''
              ? null
              : Number(load.saleUnitPrice),
          saleTotalSnapshot:
            load.saleTotalSnapshot == null || load.saleTotalSnapshot === ''
              ? null
              : Number(load.saleTotalSnapshot),
          marginSnapshot:
            load.marginSnapshot == null || load.marginSnapshot === ''
              ? null
              : Number(load.marginSnapshot),
          note: isWarrantyFlow
            ? 'La salida llegó a garantía y la unidad pasó al estado en garantía / reparación.'
            : 'La salida quedó entregada y cerró su impacto operativo en stock.',
          createdByUserId: userRole || 'transportista',
          createdByUserName: userRole || 'transportista',
          createdAt: now,
          appliedAt: now,
          cancelledAt: null
        });
      });

      const groupedByOutboundOrder = destinationLoads.reduce((acc, load) => {
        const orderId = String(load?.stockOutboundOrderId || '');
        const lineId = String(load?.stockOutboundOrderLineId || '');
        if (!orderId || !lineId) return acc;
        if (!acc[orderId]) acc[orderId] = {};
        acc[orderId][lineId] = (acc[orderId][lineId] || 0) + Math.max(1, Number(load?.quantity || 1) || 1);
        return acc;
      }, {});

      for (const [orderId, lineMap] of Object.entries(groupedByOutboundOrder)) {
        const orderRef = doc(db, resolveStockOutboundOrderDocPath(resolvedCompanyId, orderId));
        const orderSnap = await getDoc(orderRef);
        if (!orderSnap.exists()) continue;
        const currentOrder = { id: orderSnap.id, ...orderSnap.data() };
        const isWarrantyOrder = ['warranty', 'guarantee', 'repair'].includes(
          String(currentOrder.operationType || '').trim().toLowerCase()
        );
        const nextLines = (currentOrder.lines || []).map((line) => {
          const deliveredQty = Number(lineMap[String(line.id)] || 0);
          if (!deliveredQty) return line;
          const nextDelivered = Number(line.deliveredQuantity || 0) + deliveredQty;
          const nextInTransit = startedPreviously
            ? Math.max(0, Number(line.inTransitQuantity || 0) - deliveredQty)
            : Number(line.inTransitQuantity || 0);
          const nextAssigned = startedPreviously
            ? Number(line.assignedQuantity || 0)
            : Math.max(0, Number(line.assignedQuantity || 0) - deliveredQty);
          const nextPending = startedPreviously
            ? Math.max(0, Number(line.pendingDeliveryQuantity || 0))
            : Math.max(0, Number(line.pendingDeliveryQuantity || 0) - deliveredQty);
          return {
            ...line,
            deliveredQuantity: nextDelivered,
            inTransitQuantity: nextInTransit,
            assignedQuantity: nextAssigned,
            pendingDeliveryQuantity: nextPending,
            status: isWarrantyOrder
              ? 'in_warranty'
              : nextDelivered >= Number(line.quantity || 0)
                ? 'delivered'
                : 'partially_delivered',
            warrantyEnteredAt: isWarrantyOrder ? (line.warrantyEnteredAt || now) : line.warrantyEnteredAt || null,
            updatedAt: now,
          };
        });
        const summary = buildOutboundOrderHeaderSummary(currentOrder, nextLines);
        batch.update(orderRef, {
          lines: nextLines,
          status: isWarrantyOrder ? 'in_warranty' : summary.status,
          pendingDeliveryQuantity: summary.totals.pendingDeliveryQuantity,
          assignedQuantity: summary.totals.assignedQuantity,
          inTransitQuantity: summary.totals.inTransitQuantity,
          deliveredQuantity: summary.totals.deliveredQuantity,
          warrantyStatus: isWarrantyOrder ? 'in_warranty' : currentOrder.warrantyStatus || null,
          warrantyEnteredAt: isWarrantyOrder ? (currentOrder.warrantyEnteredAt || now) : currentOrder.warrantyEnteredAt || null,
          warrantyPickupApprovedAt: isWarrantyOrder ? null : currentOrder.warrantyPickupApprovedAt || null,
          updatedAt: now,
          closedAt: isWarrantyOrder ? null : summary.closedAt,
        });
      }

      await batch.commit();
  }

  async function handleReturnOutboundLoadsToPending(destinationWithTime, previousDestination = null, options = {}) {
      const destinationLoads = (destinationWithTime?.operationalLoads || []).filter(
        (load) => load?.stockProductId && load?.stockMode === 'pending_outbound'
      );
      if (!destinationLoads.length) return;

      const normalizedReason = String(options?.reason || 'replanned').trim().toLowerCase();
      const isCancellation = normalizedReason === 'cancelled';
      const startedPreviously = ['en_camino', 'en_destino'].includes(String(previousDestination?.status || ''));
      const groupedLoads = destinationLoads.reduce((acc, load) => {
        const productId = String(load?.stockProductId || '');
        if (!productId) return acc;
        const quantity = Math.max(1, Number(load?.quantity || 1) || 1);
        if (!acc[productId]) {
          acc[productId] = {
            quantity: 0,
            sampleLoad: load
          };
        }
        acc[productId].quantity += quantity;
        return acc;
      }, {});

      const batch = writeBatch(db);
      const now = new Date();

      Object.entries(groupedLoads).forEach(([productId, grouped]) => {
        const load = grouped.sampleLoad || {};
        const returnedQty = Math.max(0, Number(grouped.quantity || 0));
        if (!returnedQty) return;

        const movementRef = doc(collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId)));
        const productRef = doc(db, resolveStockProductDocPath(resolvedCompanyId, productId));
        const productUpdate = {
          'stockSummary.lastMovementAt': now,
          'stockSummary.lastMovementType': isCancellation ? 'stock_outbound_cancelled' : 'stock_outbound_replanned',
          'stockSummary.lastOperationReason': String(load.stockOperationReason || 'sale').trim().toLowerCase() || 'sale',
          'stockSummary.lastOperationType': String(load.operationType || '').trim().toLowerCase(),
          'stockSummary.lastRouteCodeSnapshot': route?.name || '',
          'stockSummary.lastDestinationNameSnapshot': destinationWithTime?.name || '',
          updatedAt: now
        };

        if (startedPreviously) {
          productUpdate['stockBuckets.outboundTransitQty'] = increment(-returnedQty);
          productUpdate['stockBuckets.pendingOutboundQty'] = increment(returnedQty);
          productUpdate['stockBuckets.physicalQty'] = increment(returnedQty);
        }

        batch.update(productRef, productUpdate);

        batch.set(movementRef, {
          companyId: resolvedCompanyId,
          productId,
          productNameSnapshot: load.stockProductNameSnapshot || load.title || '',
          skuSnapshot: load.stockSkuSnapshot || '',
          categorySnapshot: load.stockCategorySnapshot || load.category || '',
          unitSnapshot: load.stockUnitSnapshot || load.unit || 'unidad',
          movementType: isCancellation ? 'stock_outbound_cancelled' : 'stock_outbound_replanned',
          quantity: returnedQty,
          stockBucketsImpact: {
            pendingInboundDelta: 0,
            inboundTransitDelta: 0,
            physicalDelta: startedPreviously ? returnedQty : 0,
            pendingOutboundDelta: startedPreviously ? returnedQty : 0,
            outboundTransitDelta: startedPreviously ? -returnedQty : 0,
            repairDelta: 0,
            returnedDelta: 0,
            damagedDelta: 0
          },
          status: 'applied',
          sourceType: 'route',
          sourceId: route?.id || '',
          sourceLineId: String(load.id || ''),
          routeId: route?.id || '',
          routeCodeSnapshot: route?.name || '',
            transportId: route?.transporterId || route?.transportId || '',
            transportNameSnapshot: route?.transporterName || route?.transportName || route?.vehicleLabel || '',
            driverId: route?.driverId || '',
            driverNameSnapshot: [route?.driverName, route?.driverLastName].filter(Boolean).join(' ').trim(),
            driverPhotoURLSnapshot: route?.driverPhotoURL || route?.currentDriverPhotoURL || route?.photoURL || '',
            destinationId: destinationWithTime?.id || '',
          destinationNameSnapshot: destinationWithTime?.name || '',
          customerId: destinationWithTime?.contactId || '',
          customerNameSnapshot: destinationWithTime?.contactName || destinationWithTime?.name || '',
          operationType: String(load.operationType || '').trim().toLowerCase(),
          operationReason: String(load.stockOperationReason || 'sale').trim().toLowerCase() || 'sale',
          referenceCodeSnapshot: String(load.referenceCode || '').trim(),
          note: isCancellation
            ? startedPreviously
              ? 'Entrega cancelada después de salir a ruta. La mercadería vuelve a pendiente y reingresa al stock físico.'
              : 'Entrega cancelada antes de completarse. La salida vuelve a pendiente para reprogramar.'
            : startedPreviously
              ? 'Entrega incompleta. La salida vuelve a pendiente para reprogramar y reingresa al stock físico.'
              : 'Salida retirada de este destino antes de completarse. Vuelve a pendiente para reprogramar.',
          createdByUserId: userRole || 'transportista',
          createdByUserName: userRole || 'transportista',
          createdAt: now,
          appliedAt: now,
          cancelledAt: null
        });
      });

      const groupedByOutboundOrder = destinationLoads.reduce((acc, load) => {
        const orderId = String(load?.stockOutboundOrderId || '');
        const lineId = String(load?.stockOutboundOrderLineId || '');
        if (!orderId || !lineId) return acc;
        if (!acc[orderId]) acc[orderId] = {};
        acc[orderId][lineId] = (acc[orderId][lineId] || 0) + Math.max(1, Number(load?.quantity || 1) || 1);
        return acc;
      }, {});

      for (const [orderId, lineMap] of Object.entries(groupedByOutboundOrder)) {
        const orderRef = doc(db, resolveStockOutboundOrderDocPath(resolvedCompanyId, orderId));
        const orderSnap = await getDoc(orderRef);
        if (!orderSnap.exists()) continue;
        const currentOrder = { id: orderSnap.id, ...orderSnap.data() };
        const nextLines = (currentOrder.lines || []).map((line) => {
          const returnedQty = Number(lineMap[String(line.id)] || 0);
          if (!returnedQty) return line;

          const nextAssigned = startedPreviously
            ? Number(line.assignedQuantity || 0)
            : Math.max(0, Number(line.assignedQuantity || 0) - returnedQty);
          const nextInTransit = startedPreviously
            ? Math.max(0, Number(line.inTransitQuantity || 0) - returnedQty)
            : Number(line.inTransitQuantity || 0);
          const nextPending = Number(line.pendingDeliveryQuantity || 0) + returnedQty;
          const nextDelivered = Number(line.deliveredQuantity || 0);
          const nextStatus = nextDelivered > 0 ? 'partially_delivered' : 'ready_for_delivery';

          return {
            ...line,
            assignedQuantity: nextAssigned,
            inTransitQuantity: nextInTransit,
            pendingDeliveryQuantity: nextPending,
            status: nextStatus,
            updatedAt: now,
            closedAt: null,
          };
        });
        const summary = buildOutboundOrderHeaderSummary(currentOrder, nextLines);
        batch.update(orderRef, {
          lines: nextLines,
          status: summary.status,
          pendingDeliveryQuantity: summary.totals.pendingDeliveryQuantity,
          assignedQuantity: summary.totals.assignedQuantity,
          inTransitQuantity: summary.totals.inTransitQuantity,
          deliveredQuantity: summary.totals.deliveredQuantity,
          updatedAt: now,
          closedAt: null,
        });
      }

      await batch.commit();
  }

  async function handleValidateInboundReceipts(items = []) {
      const normalizedItems = (Array.isArray(items) ? items : [items]).filter(Boolean);
      if (!normalizedItems.length) return;

      setProcessingInboundValidation(true);
      try {
        const now = new Date();
        const batch = writeBatch(db);
        const nextLinesById = new Map();
        const affectedPurchaseIds = new Set();

        normalizedItems.forEach((item) => {
          const currentLine = stockPurchaseLines.find((line) => String(line.id) === String(item.purchaseLineId));
          if (!currentLine) return;
          const availableTransit = Math.max(
            0,
            Number(currentLine.pickedUpQuantity || 0) - Number(currentLine.receivedQuantity || 0)
          );
          const routeCompletedPending = Math.max(
            0,
            Number(item.routeCompletedQuantity || item.routeQuantity || 0) - Number(item.routeValidatedQuantity || 0)
          );
          const receivedDelta = Math.min(
            Math.max(0, Number(item.quantity || 0)),
            Math.max(availableTransit, routeCompletedPending)
          );
          if (!receivedDelta) return;

          affectedPurchaseIds.add(String(currentLine.purchaseId || ''));
          const nextPickedUp = Math.max(
            Number(currentLine.pickedUpQuantity || 0),
            Number(currentLine.receivedQuantity || 0) + receivedDelta
          );
          const nextReceived = Number(currentLine.receivedQuantity || 0) + receivedDelta;
          const orderedNet = Math.max(0, Number(currentLine.orderedQuantity || 0) - Number(currentLine.cancelledQuantity || 0));
          const nextStatus = nextReceived >= orderedNet ? 'received' : 'partially_received';
          const movementRef = doc(collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId)));
          const nextLine = {
            ...currentLine,
            pickedUpQuantity: nextPickedUp,
            receivedQuantity: nextReceived,
            status: nextStatus,
            lastMovementId: movementRef.id,
            lastMovementStatus: 'applied',
            updatedAt: now,
            closedAt: nextReceived >= orderedNet ? now : null
          };
          nextLinesById.set(String(currentLine.id), nextLine);

          batch.update(doc(db, resolveStockPurchaseLineDocPath(resolvedCompanyId, item.purchaseLineId)), {
            pickedUpQuantity: nextPickedUp,
            receivedQuantity: nextReceived,
            status: nextStatus,
            lastMovementId: movementRef.id,
            lastMovementStatus: 'applied',
            updatedAt: now,
            closedAt: nextReceived >= orderedNet ? now : null
          });

          if (currentLine.productId) {
            const inboundTransitResolved = Math.min(availableTransit, receivedDelta);
            const pendingInboundResolved = Math.max(0, receivedDelta - inboundTransitResolved);
            batch.update(doc(db, resolveStockProductDocPath(resolvedCompanyId, currentLine.productId)), {
              'stockBuckets.inboundTransitQty': increment(-inboundTransitResolved),
              'stockBuckets.pendingInboundQty': increment(-pendingInboundResolved),
              'stockBuckets.physicalQty': increment(receivedDelta),
              'stockSummary.lastMovementAt': now,
              'stockSummary.lastMovementType': 'purchase_received',
              'stockSummary.lastOperationReason': null,
              'stockSummary.lastOperationType': 'purchase',
              'stockSummary.lastRouteCodeSnapshot': route?.name || '',
              'stockSummary.lastDestinationNameSnapshot': 'Depósito',
              updatedAt: now
            });
          }

          batch.set(movementRef, {
            companyId: resolvedCompanyId,
            productId: currentLine.productId || '',
            productNameSnapshot: currentLine.productNameSnapshot || '',
            skuSnapshot: currentLine.skuSnapshot || '',
            categorySnapshot: currentLine.categorySnapshot || '',
            unitSnapshot: currentLine.unitSnapshot || 'unidad',
            movementType: 'purchase_received',
            quantity: receivedDelta,
            stockBucketsImpact: {
              pendingInboundDelta: -Math.max(0, receivedDelta - Math.min(availableTransit, receivedDelta)),
              inboundTransitDelta: -Math.min(availableTransit, receivedDelta),
              physicalDelta: receivedDelta,
              pendingOutboundDelta: 0,
              outboundTransitDelta: 0,
              repairDelta: 0,
              returnedDelta: 0,
              damagedDelta: 0
            },
            status: 'applied',
            sourceType: 'route',
            sourceId: route?.id || '',
            sourceLineId: item.purchaseLineId,
            purchaseId: currentLine.purchaseId || '',
            purchaseLineId: item.purchaseLineId,
            purchaseNumberSnapshot: currentLine.purchaseNumberSnapshot || currentLine.purchaseId || '',
            batchLabelSnapshot: currentLine.batchLabelSnapshot || '',
            supplierId: currentLine.supplierId || '',
            supplierNameSnapshot: currentLine.supplierNameSnapshot || '',
            routeId: route?.id || '',
            routeCodeSnapshot: route?.name || '',
              transportId: route?.transporterId || route?.transportId || '',
              transportNameSnapshot: route?.transporterName || route?.transportName || route?.vehicleLabel || '',
              driverId: route?.driverId || '',
              driverNameSnapshot: [route?.driverName, route?.driverLastName].filter(Boolean).join(' ').trim(),
              driverPhotoURLSnapshot: route?.driverPhotoURL || route?.currentDriverPhotoURL || route?.photoURL || '',
              destinationNameSnapshot: 'Depósito',
            costUnitSnapshot: Number(currentLine.unitCost || 0) > 0 ? Number(currentLine.unitCost || 0) : null,
            costTotalSnapshot: Number(currentLine.unitCost || 0) > 0 ? Number(currentLine.unitCost || 0) * receivedDelta : null,
            controllerDisplayNameSnapshot: userRole || 'Administrador',
            note:
              item.validationSource === 'route_completion_fallback'
                ? 'Ingreso validado en depósito desde cierre de ruta. Se regularizó el retiro pendiente y la mercadería impactó en el stock físico.'
                : 'Ingreso validado en depósito. La mercadería ya impacta en el stock físico.',
            createdByUserId: userRole || 'admin',
            createdByUserName: userRole || 'Administrador',
            createdAt: now,
            appliedAt: now,
            cancelledAt: null
          });
        });

        affectedPurchaseIds.forEach((purchaseId) => {
          if (!purchaseId) return;
          const purchaseLinesForPurchase = stockPurchaseLines
            .filter((line) => String(line.purchaseId || '') === purchaseId)
            .map((line) => nextLinesById.get(String(line.id)) || line);
          const purchaseSummary = buildPurchaseHeaderSummary({ id: purchaseId }, purchaseLinesForPurchase);
          batch.update(doc(db, resolveStockPurchaseDocPath(resolvedCompanyId, purchaseId)), {
            status: purchaseSummary.status,
            updatedAt: now,
            completedLinesCount: purchaseSummary.completedLinesCount,
            pendingLinesCount: purchaseSummary.pendingLinesCount,
            closedAt: purchaseSummary.closedAt
          });
        });

        const currentPendingInboundCount = Math.max(0, Number(route?.pendingInboundValidationCount || 0));
        const resolvedItemsCount = normalizedItems.length;
        const nextPendingInboundCount = Math.max(0, currentPendingInboundCount - resolvedItemsCount);
        const routeCompanyDocRef = buildCompanyRouteDocumentRef(route, resolvedCompanyId);
        if (routeCompanyDocRef) {
          batch.update(routeCompanyDocRef, {
            pendingInboundValidationCount: nextPendingInboundCount,
            awaitingInboundValidation: nextPendingInboundCount > 0,
            updatedAt: now
          });
        }

        await batch.commit();
        showMessage(
          normalizedItems.length === 1
            ? 'Ingreso validado en depósito. Ya impacta en el stock físico.'
            : `${normalizedItems.length} ingresos validados en depósito.`,
          'success'
        );
      } catch (error) {
        console.error('Error validando ingreso en depósito:', error);
        showMessage('No pude validar el ingreso al depósito.', 'error');
      } finally {
        setProcessingInboundValidation(false);
      }
  }

  const handleCardClick = (destination) => {
    if (isEditMode && (route?.status === 'pending' || route?.status === 'programmed')) return;
    openDestinationDetailDirect(destination);
  };

  const openDestinationDetailDirect = useCallback((destination) => {
    setSelectedDestination(destination);
    setDetailModalOpen(true);

    const hasUnread =
      !canManageRoute && destination.adminNotes?.some((note) => !note.read);

    if (hasUnread) {
      const updatedNotes = destination.adminNotes.map((note) => ({
        ...note,
        read: true
      }));

      handleUpdateDestination({ ...destination, adminNotes: updatedNotes }, false);
    }
  }, [canManageRoute, handleUpdateDestination]);

  const handleSaveDestinationForm = async (formData) => {
    const isEditing = routeDestinations.some((d) => d.id === formData.id);

    let updatedDestinations;
    if (isEditing) {
      updatedDestinations = routeDestinations.map((d) =>
        d.id === formData.id ? formData : d
      );
    } else {
      updatedDestinations = [
        ...routeDestinations,
        { ...formData, order: routeDestinations.length }
      ];
    }

    try {
      await syncRouteDocuments({ destinations: updatedDestinations });
      showMessage(isEditing ? 'Destino actualizado.' : 'Destino añadido.', 'success');
      setFormModalOpen(false);
      setSelectedDestination(null);
    } catch (error) {
      showMessage('Error al guardar.', 'error');
      console.error('Error al actualizar ruta:', error);
    }
  };

  const handleDeleteDestination = async (e, destId) => {
    e.stopPropagation();

    if (!window.confirm('¿Seguro que quieres eliminar este destino?')) return;

    const newDestinations = routeDestinations
      .filter((d) => d.id !== destId)
      .map((d, i) => ({ ...d, order: i }));

    try {
      await syncRouteDocuments({ destinations: newDestinations });
      showMessage('Destino eliminado.', 'success');
    } catch (error) {
      console.error('Error al eliminar destino:', error);
      showMessage('Error al eliminar.', 'error');
    }
  };

  const handleDragStart = (e, dest) => {
    setDraggedItem(dest);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async (e, targetDest) => {
    if (!draggedItem || draggedItem.id === targetDest.id) {
      setDraggedItem(null);
      return;
    }

    const previousDestinations = [...routeDestinations];
    const newDestinations = [...previousDestinations];

    const draggedIndex = newDestinations.findIndex((d) => d.id === draggedItem.id);
    const targetIndex = newDestinations.findIndex((d) => d.id === targetDest.id);

    const [reorderedItem] = newDestinations.splice(draggedIndex, 1);
    newDestinations.splice(targetIndex, 0, reorderedItem);

    const reorderedWithUpdatedOrder = newDestinations.map((d, index) => ({
      ...d,
      order: index
    }));

    setRoute((prev) => ({ ...prev, destinations: reorderedWithUpdatedOrder }));

    try {
      await syncRouteDocuments({ destinations: reorderedWithUpdatedOrder });
    } catch (error) {
      console.error('Error al reordenar destinos:', error);
      showMessage('Error al reordenar la ruta.', 'error');
      setRoute((prev) => ({ ...prev, destinations: previousDestinations }));
    }

    setDraggedItem(null);
  };

  const handleFinishRoute = async () => {
    const pendingInboundCount = Math.max(
      Number(route?.pendingInboundValidationCount || 0),
      Array.isArray(inboundValidationItems) ? inboundValidationItems.length : 0
    );

    if (pendingInboundCount > 0) {
      showMessage(
        pendingInboundCount === 1
          ? 'Todavía hay 1 ingreso pendiente de validación en depósito. Validalo antes de finalizar la ruta.'
          : `Todavía hay ${pendingInboundCount} ingresos pendientes de validación en depósito. Validalos antes de finalizar la ruta.`,
        'warning'
      );
      return;
    }

    try {
      const completedAt = new Date();
      await syncRouteDocuments({
        status: 'completed',
        active: false,
        isActive: false,
        pendingInboundValidationCount: 0,
        awaitingInboundValidation: false,
        finishedAt: completedAt,
        completedAt,
        closedAt: completedAt,
        updatedAt: completedAt
      });
      await syncLinkedServicesFinancialClosure(routeDestinations, completedAt);
      showMessage('Ruta finalizada con éxito.', 'success');
      if (typeof addLogisticsNotification === 'function') {
        addLogisticsNotification(`La ruta "${route.name}" ha sido completada.`);
      }
      setShowNextDestinationModal(false);
      if (typeof onBack === 'function') {
        onBack();
      }
    } catch (error) {
      console.error('Error al finalizar la ruta.', error);
      showMessage('Error al finalizar la ruta.', 'error');
    }
  };

  const handleCancelRoute = async (cancelReason) => {
    try {
      const now = new Date();
      const unresolvedStatuses = ['completed', 'incomplete', 'cancelled'];
      const nextDestinations = routeDestinations.map((destination) =>
        unresolvedStatuses.includes(String(destination?.status || ''))
          ? destination
          : {
              ...destination,
              status: 'cancelled',
              completedAt: destination?.completedAt || now,
            }
      );
      const unresolvedDestinations = nextDestinations.filter(
        (destination) => String(destination?.status || '') === 'cancelled' &&
          !unresolvedStatuses.includes(String((routeDestinations.find((item) => item.id === destination.id)?.status) || ''))
      );

      for (const destination of unresolvedDestinations) {
        if ((destination.operationalLoads || []).some((load) => load?.stockProductId && load?.stockMode === 'pending_outbound')) {
          await handleReturnOutboundLoadsToPending(
            { ...destination, status: 'cancelled', completedAt: now },
            routeDestinations.find((item) => item.id === destination.id) || destination,
            { reason: 'cancelled' }
          );
        }
      }

      await syncRouteDocuments({
        destinations: nextDestinations,
        status: 'cancelled',
        cancelReason,
        active: false,
        isActive: false,
        cancelledAt: now,
        closedAt: now,
        updatedAt: now
      });
      showMessage('Ruta cancelada con éxito.', 'success');
      if (typeof addLogisticsNotification === 'function') {
        addLogisticsNotification(`La ruta "${route.name}" ha sido cancelada.`);
      }
      setShowCancelModal(false);
      if (typeof onBack === 'function') {
        onBack();
      }
    } catch (error) {
      console.error('Error al cancelar la ruta.', error);
      showMessage('Error al cancelar la ruta.', 'error');
    }
  };

  const handleStartProgrammedRoute = async () => {
    try {
      await syncRouteDocuments({
        status: 'in_progress',
        startedAt: route?.startedAt || new Date()
      });
      showMessage('Ruta iniciada con éxito.', 'success');
      if (typeof addLogisticsNotification === 'function') {
        addLogisticsNotification(`La ruta programada "${route.name}" ha sido iniciada.`);
      }
    } catch (error) {
      console.error('Error al iniciar la ruta.', error);
      showMessage('Error al iniciar la ruta.', 'error');
    }
  };

  const handleSetEnCamino = async (destination) => {
    const currentEnCamino = routeDestinations.find((d) => d.status === 'en_camino');
    let newDestinations = [...routeDestinations];

    if (currentEnCamino) {
      newDestinations = newDestinations.map((d) =>
        d.id === currentEnCamino.id ? { ...d, status: 'pending' } : d
      );
    }

    newDestinations = newDestinations.map((d) =>
      d.id === destination.id ? { ...d, status: 'en_camino' } : d
    );
    const nextRouteStatus = getRouteStatus({
      ...route,
      destinations: newDestinations,
      status: 'in_progress'
    });

    try {
      await syncRouteDocuments({
        destinations: newDestinations,
        status: nextRouteStatus
      });
      setShowStartTripModal(false);
      setShowNextDestinationModal(false);
      showMessage(`Próximo destino: ${destination.name}. ¡Buen viaje!`, 'info');

      try {
        await handleMoveOutboundLoadsToTransit(destination);
      } catch (movementError) {
        console.error('La ruta arrancó, pero no pude mover las cargas a tránsito.', movementError);
        showMessage(
          'El recorrido comenzó, pero quedó pendiente actualizar algunas cargas a tránsito.',
          'warning'
        );
      }
    } catch (error) {
      console.error('Error al iniciar el recorrido hacia el destino.', error);
      showMessage('No pude activar el destino. Probá de nuevo.', 'error');
    }
  };

  const handleOpenTransporterRouteFlow = () => {
    if (route?.status === 'programmed') {
      handleStartProgrammedRoute();
      return;
    }

    if (route?.status !== 'in_progress' || !hasPendingDestinations) {
      return;
    }

    if (isFreshInProgressRoute) {
      setShowStartTripModal(true);
      return;
    }

    setShowNextDestinationModal(true);
  };

  const typeConfig = {
    Entrega: { tone: 'blue', label: 'Entrega' },
    Retiro: { tone: 'emerald', label: 'Retiro' },
    Transporte: { tone: 'violet', label: 'Transporte' },
    'Entrega y Retiro': { tone: 'orange', label: 'Entrega y Retiro' }
  };

  const contactTypeConfig = {
    cliente: { tone: 'blue', label: 'Cliente' },
    proveedor: { tone: 'emerald', label: 'Proveedor' },
    transporte: { tone: 'violet', label: 'Transporte' }
  };

  const getRouteSummary = (currentRoute) => {
    if (!currentRoute.destinations || currentRoute.destinations.length === 0) {
      return 'La ruta no tiene destinos.';
    }

    const initialOrder = currentRoute.destinations
      .map((d) => `#${d.order + 1} ${d.name}`)
      .join(', ');

    const completedDestinations = currentRoute.destinations.filter(
      (d) => d.status === 'completed'
    );
    const incompleteDestinations = currentRoute.destinations.filter(
      (d) => d.status === 'incomplete'
    );
    const cancelledDestinations = currentRoute.destinations.filter(
      (d) => d.status === 'cancelled'
    );

    let summary = `La ruta comenzó con el orden: ${initialOrder}. `;
    summary += `Se completaron ${completedDestinations.length} de ${currentRoute.destinations.length} destinos. `;

    if (completedDestinations.length > 0) {
      const lastCompleted = [...completedDestinations]
        .sort((a, b) => getCompletedAtMillis(a.completedAt) - getCompletedAtMillis(b.completedAt))
        .pop();

      if (lastCompleted) {
        const lastCompletedMillis = getCompletedAtMillis(lastCompleted.completedAt);
        summary += `El último destino completado fue "${lastCompleted.name}" a las ${new Date(
          lastCompletedMillis
        ).toLocaleTimeString('es-AR')}. `;
      }
    }

    if (incompleteDestinations.length > 0) {
      const incompleteNames = incompleteDestinations.map((d) => d.name).join(', ');
      summary += `Destinos incompletos: ${incompleteNames}. `;
    }

    if (cancelledDestinations.length > 0) {
      const cancelledNames = cancelledDestinations.map((d) => d.name).join(', ');
      summary += `Destinos cancelados: ${cancelledNames}. `;
    }

    const roadSummary = getRouteResolvedRoadSummary({
      route: currentRoute,
      completedOnly: true,
      officeLocation: resolvedOfficeLocation
    });

    summary += `Distancia total para liquidación: ${Number(roadSummary.totalDistanceKm || 0).toFixed(2)} km.`;

    if (Number(roadSummary.totalDurationMin || 0) > 0) {
      summary += ` Tiempo vial estimado: ${Math.round(roadSummary.totalDurationMin)} min.`;
    }

    if (roadSummary.hasRoadMetrics) {
      summary += ' Cálculo tomado por recorrido vial.';
    }

    return summary;
  };

  const headerStatusTone =
    route?.status === 'completed'
      ? 'emerald'
      : route?.status === 'cancelled'
      ? 'rose'
      : route?.status === 'programmed'
      ? 'violet'
      : route?.status === 'in_progress'
      ? 'blue'
      : 'slate';

  return (
    <div className="flex h-full flex-col">
      {!isTransporterView && (
        <section className="mb-5 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm md:p-6">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <div className="flex flex-wrap items-center gap-2.5">
                <button
                  onClick={onBackToDashboard || onBack}
                  className="inline-flex min-w-[11.5rem] items-center justify-center gap-2 rounded-2xl border border-slate-800 bg-[linear-gradient(180deg,#1e293b,#0f172a)] px-5 py-2.5 text-sm font-bold text-white shadow-[0_16px_30px_rgba(15,23,42,0.18)] transition hover:translate-y-[-1px] hover:bg-slate-800"
                >
                  <Icon path="M9 15L3 9m0 0l6-6M3 12h12a6 6 0 010 12h-3" className="h-4 w-4" />
                  Volver
                </button>
                <span className="text-slate-300">|</span>
                <span className="text-sm font-semibold text-slate-700">
                  Dashboard de logística
                </span>
                {userRole !== 'transportista' && onBackToHistory ? (
                  <>
                    <span className="text-slate-300">|</span>
                    <button
                      onClick={onBackToHistory}
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-slate-700 shadow-sm transition hover:-translate-y-[1px] hover:bg-slate-50"
                    >
                      <Icon path="M3.75 6.75h16.5m-16.5 5.25h16.5m-16.5 5.25h16.5" className="h-3.5 w-3.5" />
                      Ir a calendario
                    </button>
                  </>
                ) : null}
                <span className="text-slate-300">|</span>
                <InfoBadge tone={headerStatusTone}>
                  {route?.status === 'in_progress'
                    ? 'En curso'
                    : route?.status === 'programmed'
                    ? 'Programada'
                    : route?.status === 'completed'
                    ? 'Completada'
                    : route?.status === 'cancelled'
                    ? 'Cancelada'
                    : 'Pendiente'}
                </InfoBadge>
              </div>

              <h2 className="mt-4 text-3xl font-bold tracking-[-0.03em] text-slate-900 md:text-4xl">
                {route?.name}
              </h2>
            </div>

            {!isMobile && canManageRoute && (
              <div className="flex flex-wrap items-center gap-3 xl:justify-end">
                <ActionButton
                  onClick={() => setIsEditMode((p) => !p)}
                  tone={isEditMode ? 'rose' : 'blue'}
                  iconPath="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125"
                >
                  {isEditMode
                    ? 'Cerrar edición'
                    : route?.status === 'programmed'
                    ? 'Editar y reordenar'
                    : 'Reordenar ruta'}
                </ActionButton>

                {!['completed', 'cancelled'].includes(route?.status) && (
                  <>
                    {route?.status === 'programmed' ? (
                      <ActionButton
                        onClick={handleStartProgrammedRoute}
                        tone="dark"
                        iconPath="M8.25 5.25v13.5L18.75 12 8.25 5.25z"
                      >
                        Iniciar ruta
                      </ActionButton>
                    ) : (
                      <ActionButton
                        onClick={handleFinishRoute}
                        tone="emerald"
                        iconPath="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        disabled={canManageRoute && !hasPendingDestinations && inboundValidationItems.length > 0}
                        title={canManageRoute && !hasPendingDestinations && inboundValidationItems.length > 0 ? 'Validá primero los ingresos pendientes al depósito.' : ''}
                      >
                        Finalizar ruta
                      </ActionButton>
                    )}

                    <ActionButton
                      onClick={() => setShowCancelModal(true)}
                      tone="rose"
                      iconPath="M12 9.75v-.75m0 3v-.75m0 3v-.75M6.75 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM17.25 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM19.5 20.25h-15a.75.75 0 01-.75-.75V15.75a3 3 0 013-3h10.5a3 3 0 013 3v3.75c0 .414-.336.75-.75.75zM4.5 12.75V8.25a4.5 4.5 0 019-2.25m3-3a4.5 4.5 0 01-9 2.25M6 12.75V8.25m9 4.5V8.25"
                    >
                      Cancelar ruta
                    </ActionButton>
                  </>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      <div className={`grid flex-grow grid-cols-1 gap-6 ${shouldShowEmbeddedMap ? 'lg:grid-cols-5' : ''} overflow-visible`}>
        <section className={`${shouldShowEmbeddedMap ? 'lg:col-span-2' : 'lg:col-span-1'} flex min-w-0 flex-col`}>
          <div className="sticky top-0 z-20 mb-4 rounded-[24px] border border-slate-200 bg-white/95 p-4 shadow-sm backdrop-blur">
            <div className="grid grid-cols-1 gap-x-4 gap-y-2 lg:grid-cols-[minmax(0,1fr)_auto] lg:grid-rows-[auto_auto] lg:items-center">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 lg:flex-nowrap">
                  <span className="shrink-0">Planilla operativa</span>
                  <span className="h-4 w-px shrink-0 bg-slate-300" />
                  <InfoBadge tone="blue" compact>Total {routeProgress.total}</InfoBadge>
                  <InfoBadge tone="emerald" compact>Completados {routeProgress.completed}</InfoBadge>
                  {routeProgress.pending > 0 && (
                    <InfoBadge tone="amber" compact>Pendientes {routeProgress.pending}</InfoBadge>
                  )}
                </div>
              </div>
              <div className="min-w-0 lg:justify-self-end">
                <div className="flex min-w-0 items-center justify-start gap-1.5 lg:justify-end">
                  <button
                    type="button"
                    onClick={toggleGeneralListView}
                    className={`inline-flex items-center rounded-full border border-slate-200 bg-slate-50 font-bold uppercase text-slate-700 transition hover:bg-slate-100 ${isTransporterView ? 'gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.14em]' : 'gap-2 px-3 py-1.5 text-[11px] tracking-[0.16em]'}`}
                  >
                    <Icon
                      path={isGeneralListView ? 'M19.5 8.25l-7.5 7.5-7.5-7.5' : 'M4.5 15.75l7.5-7.5 7.5 7.5'}
                      className={isTransporterView ? 'h-3 w-3' : 'h-3.5 w-3.5'}
                    />
                    Vista general
                  </button>
                </div>
              </div>
              <div className="min-w-0">
                <div className="text-[15px] font-bold leading-tight text-slate-900">
                  Destinos del transporte
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/90 px-2.5 py-1">
                    <span className="font-semibold uppercase tracking-[0.14em] text-slate-400">Unidad</span>
                    <span className="font-semibold text-slate-700">{routeTransportDisplayName}</span>
                    {routeTransportMeta ? <span className="text-slate-400">{routeTransportMeta}</span> : null}
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-2 py-1">
                    {routeDriverPhotoURL ? (
                      <div className="driver-photo-shell h-8 w-8 shrink-0 rounded-[12px] border border-slate-200 shadow-[0_8px_18px_rgba(15,23,42,0.10)]">
                        <img
                          src={routeDriverPhotoURL}
                          alt={routeDriverDisplayName}
                          className="driver-photo-img h-full w-full object-cover"
                        />
                        <span className="driver-photo-glow" />
                      </div>
                    ) : (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[12px] border border-slate-200 bg-[linear-gradient(135deg,#0f172a_0%,#1d4ed8_100%)] text-[10px] font-black uppercase tracking-[0.14em] text-white shadow-[0_8px_18px_rgba(15,23,42,0.10)]">
                        {String(routeDriverDisplayName || 'Chofer')
                          .split(' ')
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((part) => part.charAt(0).toUpperCase())
                          .join('') || 'CH'}
                      </div>
                    )}
                    <span className="font-semibold uppercase tracking-[0.14em] text-slate-400">Chofer</span>
                    <span className="font-semibold text-slate-700">{routeDriverDisplayName}</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenTransportProfile}
                    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] transition ${hasTransportProfileContext ? 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100' : 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'}`}
                    title={hasTransportProfileContext ? 'Abrir ficha de unidad' : 'Ruta sin unidad vinculada'}
                    disabled={!hasTransportProfileContext}
                  >
                    <Icon path="M13.5 6H18m0 0v4.5M18 6l-6.75 6.75M6 18h4.5M6 18v-4.5M6 18l6.75-6.75" className="h-3 w-3" />
                    Ficha
                  </button>
                </div>
              </div>
              <div className="min-w-0 lg:justify-self-end">
                {!isTransporterView && sortedDestinations.length > 0 ? (
                  <div className="no-scrollbar flex min-w-0 items-center justify-start gap-1 overflow-x-auto lg:justify-end">
                    {sortedDestinations.map((destination) => {
                      const destinationNumber = Number(destination.order ?? 0) + 1;
                      const isCollapsed = collapsedDestinationIds.has(destination.id);

                      return (
                        <button
                          key={`mini-${destination.id}`}
                          type="button"
                          onClick={() => focusDestinationFromMiniPill(destination.id)}
                          title={`${destination.name || 'Destino'} · Ir y ${isCollapsed ? 'desplegar' : 'ver'}`}
                          className={`relative inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[9px] font-black leading-none transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.04] ${
                            getDestinationMiniPillClass(destination.status)
                          } ${isCollapsed ? 'opacity-72 grayscale-[0.08]' : 'ring-1 ring-white/80'}`}
                        >
                          <span className="pointer-events-none absolute inset-[1px] rounded-full border border-white/10" />
                          <span className="relative">{destinationNumber}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div />
                )}
              </div>
              <div className="lg:col-span-2">
                <div className="flex min-w-0 items-center justify-center">
                  {isTransporterView && route?.status === 'programmed' && (
                    <ActionButton
                      onClick={handleOpenTransporterRouteFlow}
                      tone="dark"
                      iconPath="M8.25 5.25v13.5L18.75 12 8.25 5.25z"
                      className="w-full justify-center px-3 py-2 text-[12px] sm:w-auto sm:px-3.5 sm:py-2.5 sm:text-[13px]"
                    >
                      Iniciar ruta
                    </ActionButton>
                  )}
                  {isTransporterView && route?.status === 'in_progress' && hasPendingDestinations && (
                    <ActionButton
                      onClick={handleOpenTransporterRouteFlow}
                      tone="cyan"
                      iconPath="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                      className="w-full justify-center border border-cyan-200 bg-cyan-50 px-3 py-2 text-[12px] text-cyan-700 hover:bg-cyan-100 sm:w-auto sm:px-3.5 sm:py-2.5 sm:text-[13px]"
                    >
                      Ruta siguiente
                    </ActionButton>
                  )}
                </div>
                {isEditMode && canManageRoute && (
                  <ActionButton
                    onClick={() => {
                      setSelectedDestination(null);
                      setFormModalOpen(true);
                    }}
                    tone="blue"
                    iconPath="M12 9v6m3-3H9"
                    className="ml-1"
                  >
                    Añadir destino
                  </ActionButton>
                )}
              </div>
            </div>
          </div>
          <div
            ref={destinationListContainerRef}
            className="flex-grow overflow-y-auto pr-2 max-h-[calc(100vh-220px)] scroll-pb-28"
          >
            <div className="space-y-4 pb-28">
              {sortedDestinations.map((dest, index) => {
                const typeMeta = typeConfig[dest.type] || {
                  tone: 'slate',
                  label: dest.type || 'Operación'
                };

                const contactMeta = contactTypeConfig[dest.contactType] || {
                  tone: 'slate',
                  label: 'Contacto'
                };

                const statusMeta = getDestinationStatusMeta(dest.status);
                const isStatusNotifying = statusNotification.id === dest.id;
                const linkColor = dest.deliversTo
                  ? routeDestinations.find((d) => d.id === dest.deliversTo)
                    ? '#4f46e5'
                    : '#ef4444'
                  : null;
                const isCollapsed = collapsedDestinationIds.has(dest.id);
                const destinationAddress = [dest.address, dest.apartment && `(${dest.apartment})`]
                  .filter(Boolean)
                  .join(' ');
                const destinationLocation = [dest.locality, dest.partido, dest.province, dest.postalCode]
                  .filter(Boolean)
                  .join(', ');
                const destinationTraceEvents = (traceEventsByDestinationId.get(dest.id) || []).slice(0, 4);

                return (
                  <div
                    key={dest.id}
                    ref={(node) => {
                      if (node) {
                        destinationItemRefs.current[dest.id] = node;
                      } else {
                        delete destinationItemRefs.current[dest.id];
                      }
                    }}
                    style={{ animationDelay: `${index * 45}ms` }}
                    className="animate-fade-in-down"
                  >
                    <div
                      draggable={
                        isEditMode &&
                        (route?.status === 'pending' || route?.status === 'programmed')
                      }
                      onDragStart={(e) =>
                        isEditMode &&
                        (route?.status === 'pending' || route?.status === 'programmed') &&
                        handleDragStart(e, dest)
                      }
                      onDragOver={isEditMode ? (e) => e.preventDefault() : undefined}
                      onDrop={(e) => isEditMode && handleDrop(e, dest)}
                      className={`group relative overflow-hidden rounded-[26px] border p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${
                        isEditMode &&
                        (route?.status === 'pending' || route?.status === 'programmed')
                          ? 'cursor-grab'
                          : 'cursor-default'
                      } ${statusMeta.shell} ${draggedItem?.id === dest.id ? 'opacity-50' : ''}`}
                    >
                      <span className="pointer-events-none absolute inset-y-0 left-[-32%] z-0 w-[28%] skew-x-[-20deg] bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.78),transparent)] opacity-0 transition-transform duration-1000 group-hover:translate-x-[420%] group-hover:opacity-100" />
                      <div className="absolute inset-x-0 top-0 h-1 bg-slate-200" />
                      {dest.status === 'completed' && (
                        <div className="absolute inset-x-0 top-0 h-1 bg-emerald-400" />
                      )}
                      {dest.status === 'incomplete' && (
                        <div className="absolute inset-x-0 top-0 h-1 bg-amber-400" />
                      )}
                      {dest.status === 'cancelled' && (
                        <div className="absolute inset-x-0 top-0 h-1 bg-rose-400" />
                      )}
                      {dest.status === 'en_destino' && (
                        <div className="absolute inset-x-0 top-0 h-1 bg-blue-400" />
                      )}
                      {dest.status === 'en_camino' && (
                        <div className="absolute inset-x-0 top-0 h-1 bg-cyan-500" />
                      )}

                      <style>{`
                        @keyframes fadeIn {
                          from { opacity: 0; transform: translateY(10px); }
                          to { opacity: 1; transform: translateY(0); }
                        }
                        @keyframes destinationNumberGlow {
                          0%, 82%, 100% { box-shadow: 0 10px 24px rgba(15,23,42,0.22), inset 0 0 0 rgba(255,255,255,0); }
                          88% { box-shadow: 0 14px 30px rgba(15,23,42,0.28), 0 0 0 1px rgba(255,255,255,0.22), 0 0 18px rgba(255,255,255,0.18); }
                          92% { box-shadow: 0 18px 36px rgba(15,23,42,0.34), 0 0 20px rgba(148,163,184,0.22); }
                        }
                        .animate-fade-in-down {
                          animation: fadeIn 0.45s ease-out forwards;
                        }
                      `}</style>

                      {linkColor && (
                        <div
                          className="absolute right-4 top-4 h-3 w-3 rounded-full ring-4 ring-white"
                          style={{ backgroundColor: linkColor }}
                          title="Destino vinculado"
                        />
                      )}

                      {isStatusNotifying && (
                        <div
                          className="absolute right-10 top-4 text-amber-500 animate-ping"
                          title={`Estado cambiado a ${statusNotification.type}`}
                        >
                          <span className="relative flex h-3 w-3">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500"></span>
                          </span>
                        </div>
                      )}

                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="rounded-[22px] border border-slate-200/80 bg-white/95 px-4 py-3 shadow-[0_14px_32px_rgba(15,23,42,0.08)]">
                            <button
                              type="button"
                              onClick={() => toggleDestinationCollapsed(dest.id)}
                              className="flex w-full items-start justify-between gap-3 rounded-[18px] border border-slate-200/80 bg-[linear-gradient(180deg,#f8fafc,#eef2f7)] px-3 py-2.5 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] transition hover:bg-[linear-gradient(180deg,#f1f5f9,#e2e8f0)]"
                            >
                              <div className="flex min-w-0 flex-1 items-start gap-3">
                                <div
                                  className="flex h-12 min-w-[3.25rem] shrink-0 items-center justify-center rounded-[16px] bg-slate-900 text-lg font-black tracking-[-0.03em] text-white shadow-[0_10px_24px_rgba(15,23,42,0.22)]"
                                  style={{
                                    animation: 'destinationNumberGlow 6s ease-in-out infinite',
                                    animationDelay: `${index * 0.45}s`
                                  }}
                                >
                                  {dest.order + 1}
                                </div>

                                <div className="min-w-0 flex-1">
                                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                                    <h4
                                      className={`truncate text-xl font-bold tracking-[-0.03em] text-slate-900 ${
                                        dest.status !== 'pending' && dest.status !== 'en_camino'
                                          ? 'line-through decoration-1'
                                          : ''
                                      }`}
                                    >
                                      {dest.name}
                                    </h4>
                                    <span className="hidden h-5 w-px bg-slate-200 sm:block" />
                                    <p className="truncate text-sm font-medium text-slate-700">
                                      {destinationAddress}
                                    </p>
                                    {dest.contactName ? (
                                      <>
                                        <span className="hidden h-5 w-px bg-slate-200 sm:block" />
                                        <span className="truncate text-sm font-semibold text-slate-700">
                                          {dest.contactName}
                                        </span>
                                      </>
                                    ) : null}
                                    {dest.contactPhone ? (
                                      <a
                                        href={`tel:${String(dest.contactPhone).replace(/\s+/g, '')}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="group relative inline-flex items-center gap-1.5 overflow-hidden rounded-full border border-rose-200 bg-[linear-gradient(180deg,#fff1f2,#ffe4e6)] px-3 py-1.5 text-xs font-bold text-rose-700 shadow-[0_10px_24px_rgba(244,63,94,0.16)] transition hover:bg-rose-50"
                                        title="Llamar"
                                      >
                                        <span className="pointer-events-none absolute inset-y-0 left-[-30%] w-1/3 skew-x-[-20deg] bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.88),transparent)] opacity-80 transition-transform duration-700 group-hover:translate-x-[360%]" />
                                        <Icon
                                          path="M2.25 4.5A2.25 2.25 0 014.5 2.25h2.386c.51 0 .957.343 1.09.836l.784 2.94a1.125 1.125 0 01-.417 1.16l-1.294.97a11.035 11.035 0 005.198 5.198l.97-1.294a1.125 1.125 0 011.16-.417l2.94.784c.493.133.836.58.836 1.09V19.5a2.25 2.25 0 01-2.25 2.25h-.75C8.32 21.75 2.25 15.68 2.25 8.25V7.5v-3z"
                                          className="relative z-10 h-3.5 w-3.5 fill-rose-500 text-rose-500"
                                        />
                                        <span className="relative z-10">{dest.contactPhone}</span>
                                      </a>
                                    ) : null}
                                  </div>

                                  <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm leading-relaxed text-slate-600">
                                    {destinationLocation ? <span>{destinationLocation}</span> : null}
                                  </div>
                                </div>
                              </div>

                              <span className="ml-2 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 shadow-sm transition group-hover:bg-slate-50">
                                <Icon
                                  path={isCollapsed ? 'M19.5 8.25l-7.5 7.5-7.5-7.5' : 'M4.5 15.75l7.5-7.5 7.5 7.5'}
                                  className="h-3.5 w-3.5"
                                />
                              </span>
                            </button>

                            <div
                              className={`origin-top transition-all duration-300 ${
                                isCollapsed ? 'max-h-0 scale-y-95 overflow-hidden opacity-0' : 'mt-3 max-h-[1200px] scale-y-100 opacity-100'
                              }`}
                            >
                              {isEditMode && canManageRoute && (
                                <div className="ml-2 flex shrink-0 flex-col items-center gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedDestination(dest);
                                      setFormModalOpen(true);
                                    }}
                                    className="flex h-10 w-10 items-center justify-center rounded-full border border-blue-200 bg-white text-blue-600 transition hover:bg-blue-50"
                                  >
                                    <Icon
                                      path="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125"
                                      className="h-5 w-5"
                                    />
                                  </button>

                                  <button
                                    onClick={(e) => handleDeleteDestination(e, dest.id)}
                                    className="flex h-10 w-10 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-600 transition hover:bg-rose-50"
                                  >
                                    <Icon
                                      path="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                      className="h-5 w-5"
                                    />
                                  </button>
                                </div>
                              )}
                            
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              <InfoBadge tone={statusMeta.tone}>{statusMeta.label}</InfoBadge>
                              <InfoBadge tone={typeMeta.tone}>{typeMeta.label}</InfoBadge>
                              {dest.contactType && (
                                <InfoBadge tone={contactMeta.tone}>{contactMeta.label}</InfoBadge>
                              )}
                              {dest.priority === 'Urgente' && (
                                <InfoBadge tone="rose">Urgente</InfoBadge>
                              )}
                              {dest.priority === 'Importante' && (
                                <InfoBadge tone="amber">Importante</InfoBadge>
                              )}
                              {dest.status === 'en_destino' && (
                                <InfoBadge tone="blue">Presente en parada</InfoBadge>
                              )}
                              {dest.status === 'en_camino' && (
                                <span className="inline-flex animate-pulse items-center gap-1 rounded-full border border-amber-200 bg-[linear-gradient(180deg,#fff8e6,#fef3c7)] px-2.5 py-1 text-[11px] font-semibold text-amber-700 shadow-[0_6px_18px_rgba(251,191,36,0.16)]">
                                  Próxima parada activa
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDestinationDetailDirect(dest);
                                }}
                                className="ml-auto inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-700 transition hover:bg-slate-100"
                              >
                                Ver detalle
                              </button>
                            </div>

                            {dest.serviceLinked || dest.serviceId ? (
                              <div className={`mt-4 flex flex-wrap items-center gap-2 rounded-2xl border px-3 py-2 ${getServiceBadgeTheme(dest.serviceId).shell}`}>
                                <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] ${getServiceBadgeTheme(dest.serviceId).soft}`}>
                                  {dest.serviceReferenceCode || `Servicio ${String(dest.serviceId || '').slice(0, 6).toUpperCase()}`}
                                </span>
                                <span className={`text-xs font-bold ${getServiceBadgeTheme(dest.serviceId).text}`}>
                                  {dest.contactName || 'Cliente sin nombre'}
                                </span>
                                <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${getServiceBadgeTheme(dest.serviceId).soft}`}>
                                  {dest.serviceTaskLabel || dest.serviceTask || 'Tarea'}
                                </span>
                                <span className="rounded-full border border-white/70 bg-white/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-600">
                                  {dest.serviceTransportLabel || dest.serviceTypeLabel || dest.serviceTransport || dest.serviceType || 'Transporte'}
                                </span>
                              </div>
                            ) : null}

                            {(String(dest.photoURL || '').trim() || (Array.isArray(dest.evidenceFiles) && dest.evidenceFiles.length > 0)) ? (
                              <div className="mt-4 rounded-2xl border border-cyan-200 bg-[linear-gradient(180deg,#ecfeff,#f8fafc)] p-3 shadow-sm">
                                <div className="flex flex-wrap items-start gap-3">
                                  {String(dest.photoURL || '').trim() ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDestinationDetailDirect(dest);
                                      }}
                                      className="overflow-hidden rounded-2xl border border-cyan-200 bg-white shadow-sm transition hover:-translate-y-0.5"
                                      title="Abrir detalle de evidencia"
                                    >
                                      <img
                                        src={String(dest.photoURL).trim()}
                                        alt={`Evidencia de ${dest.name || 'destino'}`}
                                        className="h-20 w-24 object-cover"
                                      />
                                    </button>
                                  ) : null}

                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="rounded-full border border-cyan-200 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-cyan-700">
                                        Evidencia operativa
                                      </span>
                                      <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-600">
                                        {(dest.evidenceFiles || []).length || (String(dest.photoURL || '').trim() ? 1 : 0)} archivo{((dest.evidenceFiles || []).length || (String(dest.photoURL || '').trim() ? 1 : 0)) === 1 ? '' : 's'}
                                      </span>
                                      {String(dest.photoURL || '').trim() ? (
                                        <a
                                          href={String(dest.photoURL).trim()}
                                          download
                                          target="_blank"
                                          rel="noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-700 transition hover:border-cyan-200 hover:text-cyan-700"
                                        >
                                          <Icon path="M12 16.5v-9m0 9l-3.75-3.75M12 16.5l3.75-3.75M3.75 19.5h16.5" className="h-3.5 w-3.5" />
                                          Descargar
                                        </a>
                                      ) : null}
                                    </div>
                                    <p className="mt-2 text-sm font-semibold text-slate-800">
                                      {dest.latestEvidence?.label || 'Última evidencia cargada por chofer'}
                                    </p>
                                    <p className="mt-1 text-xs leading-relaxed text-slate-600">
                                      Esta parada ya dejó respaldo visual. Abrí el detalle para revisar el historial completo de evidencias.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ) : null}

                            {destinationTraceEvents.length > 0 ? (
                              <div className="mt-4 rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#ffffff,#f8fafc)] p-3 shadow-sm">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div>
                                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-slate-700">
                                      Trazabilidad operativa
                                    </span>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">
                                      Últimas acciones registradas en este destino
                                    </p>
                                  </div>
                                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-600">
                                    {destinationTraceEvents.length} evento{destinationTraceEvents.length === 1 ? '' : 's'}
                                  </span>
                                </div>

                                <div className="mt-3 space-y-2">
                                  {destinationTraceEvents.map((event) => {
                                    const eventMeta = buildTraceEventMeta(event);
                                    return (
                                      <div
                                        key={event.id}
                                        className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2.5"
                                      >
                                        <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border ${
                                          eventMeta.tone === 'cyan'
                                            ? 'border-cyan-200 bg-cyan-50 text-cyan-700'
                                            : eventMeta.tone === 'emerald'
                                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                              : eventMeta.tone === 'amber'
                                                ? 'border-amber-200 bg-amber-50 text-amber-700'
                                                : eventMeta.tone === 'rose'
                                                  ? 'border-rose-200 bg-rose-50 text-rose-700'
                                                  : 'border-slate-200 bg-slate-50 text-slate-700'
                                        }`}>
                                          <Icon path={eventMeta.iconPath} className="h-4 w-4" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.16em] ${
                                              eventMeta.tone === 'cyan'
                                                ? 'border-cyan-200 bg-cyan-50 text-cyan-700'
                                                : eventMeta.tone === 'emerald'
                                                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                                  : eventMeta.tone === 'amber'
                                                    ? 'border-amber-200 bg-amber-50 text-amber-700'
                                                    : eventMeta.tone === 'rose'
                                                      ? 'border-rose-200 bg-rose-50 text-rose-700'
                                                      : 'border-slate-200 bg-slate-50 text-slate-700'
                                            }`}>
                                              {eventMeta.badge}
                                            </span>
                                            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                                              {formatTraceEventDateTime(event.createdAt)}
                                            </span>
                                            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-600">
                                              {event.createdByRole || 'operador'}
                                            </span>
                                          </div>
                                          <p className="mt-1 text-sm font-semibold text-slate-900">
                                            {eventMeta.title}
                                          </p>
                                          <p className="mt-0.5 break-all text-xs leading-relaxed text-slate-600">
                                            {eventMeta.detail}
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : null}

                            {(dest.description || dest.deliveries || dest.pickups || dest.schedule || dest.deliversTo) && (
                              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                                {dest.description && (
                                  <div className="rounded-2xl bg-white/80 px-3 py-3 text-sm text-slate-600 shadow-sm">
                                    <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                      Detalle
                                    </span>
                                    <span className="mt-1 block">{dest.description}</span>
                                  </div>
                                )}

                                {(Number(dest.deliveries) > 0 || Number(dest.pickups) > 0) && (
                                  <div className="rounded-2xl bg-white/80 px-3 py-3 text-sm shadow-sm">
                                    <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                      Carga operativa
                                    </span>
                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-slate-700">
                                      {Number(dest.deliveries) > 0 ? (
                                        <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">
                                          Entregas: {Number(dest.deliveries)}
                                        </span>
                                      ) : null}
                                      {Number(dest.pickups) > 0 ? (
                                        <span className="rounded-full bg-cyan-50 px-2.5 py-1 font-semibold text-cyan-700">
                                          Retiros: {Number(dest.pickups)}
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>
                                )}

                                {(dest.incomingOperationalLoads || []).length > 0 && (
                                  <div className="rounded-2xl bg-cyan-50 px-3 py-3 text-sm shadow-sm">
                                    <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-700">
                                      Recibe carga vinculada
                                    </span>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {dest.incomingOperationalLoads.map((incoming) => (
                                        <span
                                          key={`${incoming.sourceDestinationId}-${incoming.loadId}`}
                                          className="rounded-full border border-cyan-200 bg-white px-3 py-1.5 text-xs font-bold text-cyan-700"
                                        >
                                          {resolveIncomingOperationalLoadLabel(incoming)} · retirado en {incoming.sourceDestinationName || 'otro punto'}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {dest.schedule && (
                                  <div className="rounded-2xl bg-white/80 px-3 py-3 text-sm text-cyan-700 shadow-sm">
                                    <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                      Franja horaria
                                    </span>
                                    <span className="mt-1 inline-flex items-center gap-1">
                                      <Icon
                                        path="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                                        className="h-4 w-4"
                                      />
                                      {dest.schedule}
                                    </span>
                                  </div>
                                )}

                                {(dest.contactName || dest.contactPhone) && (
                                  <div className="rounded-2xl bg-white/80 px-3 py-3 text-sm text-slate-700 shadow-sm">
                                    <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                      Contacto del destino
                                    </span>
                                    <div className="mt-1 space-y-1">
                                      {dest.contactName ? <p className="font-semibold text-slate-900">{dest.contactName}</p> : null}
                                      {dest.contactPhone ? <p>{dest.contactPhone}</p> : null}
                                    </div>
                                  </div>
                                )}

                                {dest.deliversTo && (
                                  <div className="rounded-2xl bg-white/80 px-3 py-3 text-sm shadow-sm">
                                    <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                      Destino vinculado
                                    </span>
                                    <div
                                      className={`mt-1 inline-flex items-center gap-1 ${
                                        dest.deliversTo === 'oficina'
                                          ? 'text-slate-700'
                                          : 'text-rose-600'
                                      }`}
                                    >
                                      {dest.deliversTo === 'oficina' ? (
                                        <Icon
                                          path="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h6M9 11.25h6M9 15.75h6"
                                          className="h-4 w-4"
                                        />
                                      ) : (
                                        <span>→</span>
                                      )}
                                      Llevar a:{' '}
                                      {dest.deliversTo === 'oficina'
                                        ? 'Oficina'
                                        : routeDestinations.find((d) => d.id === dest.deliversTo)?.name || 'N/A'}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {isEditMode && canManageRoute && (
                <button
                  onClick={() => {
                    setSelectedDestination(null);
                    setFormModalOpen(true);
                  }}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-[24px] border-2 border-dashed border-blue-300 bg-blue-50 py-4 text-sm font-bold text-blue-700 transition hover:bg-blue-100"
                >
                  <Icon path="M12 9v6m3-3H9" className="h-5 w-5" />
                  Añadir nuevo destino a la hoja
                </button>
              )}
            </div>
          </div>
        </section>

        {shouldShowInboundValidationPanel && (
          <section className="min-w-0 lg:col-span-3">
            <div className="rounded-[28px] border border-amber-200 bg-[linear-gradient(180deg,#fff8eb_0%,#ffffff_100%)] p-5 shadow-[0_18px_40px_rgba(245,158,11,0.10)]">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">Ingreso a depósito</p>
                  <h4 className="mt-1 text-xl font-bold text-slate-900">Productos en tránsito de entrada</h4>
                  <p className="mt-2 text-sm text-slate-600">
                    El conductor ya completó el retiro. Validá acá su llegada al depósito para que impacten en el stock físico.
                  </p>
                </div>
                <ActionButton
                  onClick={() => handleValidateInboundReceipts(inboundValidationItems)}
                  tone="amber"
                  iconPath="M4.5 12.75l6 6 9-13.5"
                  disabled={processingInboundValidation || inboundValidationItems.length === 0}
                >
                  {processingInboundValidation ? 'Validando...' : 'Validar todos los ingresos'}
                </ActionButton>
              </div>

              <div className="mt-4 space-y-3">
                {inboundValidationItems.length === 0 ? (
                  <div className="rounded-[22px] border border-amber-200 bg-white px-4 py-4 text-sm text-slate-600 shadow-sm">
                    Detectamos ingresos pendientes en la ruta. Estamos sincronizando el detalle de los productos para habilitar la validación.
                  </div>
                ) : null}

                {inboundValidationItems.map((item) => (
                  <div key={item.purchaseLineId} className="rounded-[22px] border border-amber-200 bg-white px-4 py-4 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-base font-bold text-slate-900">{item.productName}</p>
                          <InfoBadge tone="amber">{item.quantity} {item.unit}</InfoBadge>
                          {item.sku ? <InfoBadge tone="slate">SKU {item.sku}</InfoBadge> : null}
                        </div>
                        <p className="mt-2 text-sm text-slate-600">
                          Proveedor: <span className="font-semibold text-slate-800">{item.supplierName || 'Proveedor'}</span>
                          {item.destinationNames?.length ? ` · Retirado en ${item.destinationNames.join(', ')}` : ''}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">Al validar, deja de estar en tránsito de entrada y pasa a guardado en depósito.</p>
                      </div>
                      <ActionButton
                        onClick={() => handleValidateInboundReceipts(item)}
                        tone="emerald"
                        iconPath="M4.5 12.75l6 6 9-13.5"
                        disabled={processingInboundValidation}
                      >
                        Validar ingreso
                      </ActionButton>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {shouldShowEmbeddedMap && (

          <section className="min-w-0 lg:col-span-3 lg:sticky lg:top-5 lg:self-start">
            <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
                <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                  <span>Vista territorial</span>
                  <span className="h-4 w-px bg-slate-300" />
                  <span className="text-base font-bold normal-case tracking-normal text-slate-900">Mapa de la ruta</span>
                </div>
              </div>

              <div className="h-[320px] sm:h-[360px] lg:h-[420px] xl:h-[500px]">
                <MapComponent
                  destinations={routeDestinations}
                  officeLocation={resolvedOfficeLocation}
                  transporterLocation={transporterLocation}
                  transporterPath={transporterPath}
                  routeRoadMetrics={routeRoadMetrics}
                  activeLeg={{
                    from: lastCompletedDestination,
                    to: routeDestinations.find((d) => d.status === 'en_camino')
                  }}
                />
              </div>
            </div>
          </section>
        )}
      </div>

      {route?.status === 'completed' && (
        <div className="mt-5 flex-shrink-0">
          <button
            onClick={() => setShowSummary((p) => !p)}
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-bold text-slate-800 transition hover:bg-slate-200"
          >
            <Icon
              path={
                showSummary
                  ? 'M4.5 15.75l7.5-7.5 7.5 7.5'
                  : 'M19.5 8.25l-7.5 7.5-7.5 7.5'
              }
              className="h-5 w-5"
            />
            {showSummary ? 'Ocultar resumen del día' : 'Ver resumen del día'}
          </button>

          <div
            className={`origin-top transition-all duration-500 ${
              showSummary ? 'mt-4 scale-y-100 max-h-[520px]' : 'mt-0 scale-y-0 max-h-0 overflow-hidden'
            }`}
          >
            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                Cierre operativo
              </p>
              <h4 className="mt-2 text-2xl font-bold text-slate-900">Resumen de la ruta</h4>
              <p className="mt-3 text-sm leading-relaxed text-slate-700">{getRouteSummary(route)}</p>
            </div>
          </div>
        </div>
      )}

      {detailModalOpen && (
        <DestinationDetailModal
          destination={selectedDestination}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedDestination(null);
          }}
          onUpdate={handleUpdateDestination}
          showMessage={showMessage}
          userRole={userRole}
          companyId={resolvedCompanyId}
          routeId={route?.id}
          route={route}
          destinationIndex={routeDestinations.findIndex((d) => d.id === selectedDestination?.id)}
          destinationCount={routeDestinations.length}
        />
      )}

      {formModalOpen && (
        <DestinationFormModal
          route={route}
          destination={selectedDestination}
          onClose={() => {
            setFormModalOpen(false);
            setSelectedDestination(null);
          }}
          showMessage={showMessage}
          contacts={allContacts}
          onSave={handleSaveDestinationForm}
          companyId={resolvedCompanyId}
        />
      )}

      {showCancelModal && (
        <CancelRouteModal
          onClose={() => setShowCancelModal(false)}
          onCancelRoute={handleCancelRoute}
        />
      )}

      {isTransporterView && showStartTripModal && (
        <StartTripModal
          destinations={routeDestinations.filter((d) => d.status === 'pending')}
          onStart={handleSetEnCamino}
          onClose={() => setShowStartTripModal(false)}
        />
      )}

      {isTransporterView && showNextDestinationModal && (
        <NextDestinationModal
          destinations={routeDestinations.filter((d) => d.status === 'pending')}
          onNext={handleSetEnCamino}
          onClose={() => setShowNextDestinationModal(false)}
        />
      )}
    </div>
  );
};

export default RouteDetail;
